import React, { useEffect, useMemo, useState } from "react";

import useTheme from "hooks/observer";

import { useYsentinel } from "../data/YsentinelDataContext";
import { buildUserBundle, rangeWindow } from "../data/defaults";

import { ChartCard, Badge, EmptyHint, ErrorHint, Loader, riskTone } from "./_shared/primitives";
import EChart, { echartsPalette, OP_CLASS_COLORS, TABLEAU_10 } from "./_shared/EChart";
import StepNavigator from "./_shared/StepNavigator";
import { detectAnomalies } from "./_shared/anomalies";
import { composeRiskScore, bandTone } from "./_shared/riskScore";
import UserTeamPickerModal from "./UserTeamPickerModal";
import { OPERATION_CLASS } from "./_shared/constants";
import { relativeTime, humanBytes } from "./_shared/utils";
const FILES = []; // Decoupled from mockData.js
import { ManageUser as MU, Team as izTeam, GetRemovedTeamsFromLogs, GetRemovedUsersFromLogs, UserCountriesAccessedFrom, UserOpCount, UserWriteActivity, UserActivityTimeline, TeamActivityTimeline, UserStorageBreakdown, UserFilesTouched, TeamFilesTouched, UserSharingActions, UserPermissionRecieved, TeamMembershipChanges, TeamRoleIntersection, TeamMembers, UserDeniedAttempts, UserRiskScore, UserTopContributingSignals, UserAnomalies, UserOverview, UserTimeHeatmap, ListFilesForDashboard } from "../data/queuedSdk";
import {getEmail} from "@induz/sdk";

import "./_shared/dashboard.scss";
import "./_shared/step-navigator.scss";
import "./users-dashboard.scss";

// =============================================================================
// Helpers
// =============================================================================

// Flatten OPERATION_CLASS so we can look up the class for any op in O(1).
// Used by the OpMix donut to colour each slice by its class.
const OP_TO_CLASS = (() => {
  const m = {};
  Object.entries(OPERATION_CLASS).forEach(([cls, ops]) => {
    ops.forEach((op) => { m[op] = cls; });
  });
  return m;
})();

// Convert an AuditLogRow (the shape stored in bundle.events) into the StepNode
// shape the StepNavigator expects (`op`, `actor`, `at`, `detail`, `country`,
// `success`, `permission_granted_by`). The navigator collapses GetChunk runs
// internally so we don't need to dedupe here.
const toStep = (row) => ({
  at: row.timestamp,
  op: row.operation,
  actor: row.user_email,
  country: row.country,
  detail: row.message,
  success: row.success,
  permission_granted_by: row.permission_granted_by,
});

// "Meaningful" ops shown in the activity timeline. Reads are collapsed by the
// navigator anyway, but we exclude them from the timeline shape so the cards
// surface decisions and exposures rather than view-counts. Adjust per panel
// later once we have noise complaints from analysts.
const MEANINGFUL_OPS = new Set([
  "StartWrite", "FinalizeWrite", "RestoreVersion", "RemoveVersion",
  "Share", "UpdateShare", "CopyFile", "MoveFile", "MoveFolder",
  "RenameFile", "RenameFolder", "CreateFolder",
  "AddRole", "RemoveRole", "SetRolesToUser",
  "AddTeam", "UpdateTeam", "RemoveTeam",
  "BlockUser", "UnblockUser", "RemoveOrganizationUser",
  "AddComment", "UpdateComment", "RemoveComment",
  "UpdateTags",
  // GetChunk included so denied reads still show up — collapsed by navigator.
  "GetChunk",
]);

const STATUS_TONE = { active: "success", dormant: "warning", blocked: "danger", suspended: "neutral", removed: "danger" };

// =============================================================================
// Component
// =============================================================================

const UsersDashboard = () => {
  const mode = useTheme();
  const palette = echartsPalette(mode);

  const {
    users: usersSlice,
    files: filesSlice,
    goToDashboard,
    range,
  } = useYsentinel();

  const {
    users: defaultUsers,
    teams: defaultTeams,
    userActivity,
    teamActivity,
    knownDomains,
    selectedSubject,
    selectedSubjectStack,
    recentSubjects,
    setSelectedSubject,
    pushSubject,
    popSubject,
    pushRecentSubject,
  } = usersSlice;

  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [removedTeams, setRemovedTeams] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchUsers = async () => {
      try {
        // Use a 30-day window for removed entities
        const now = Date.now();
        const thirtyDaysAgo = now - 30 * 86_400_000;

        const [userList, /* teamNames, removedTeamNames,  removedUserEmails*/] = await Promise.all([
          MU.listUsers(),
          // izTeam.listTeams(),
          // GetRemovedTeamsFromLogs(thirtyDaysAgo, now).catch(() => []),
          GetRemovedUsersFromLogs(thirtyDaysAgo, now).catch(() => []),
        ]);
        if (!active) return;

        let actualUsers = userList.map((user) => ({
          is_actual: true,
          email: user.email,
          firstname: user.name ? user.name.split(" ")[0] : user.email.split("@")[0],
          lastname: user.name ? user.name.split(" ").slice(1).join(" ") : "",
          role: user.roles?.[0] || "",
          teams: user.teams || [],
          active: !user.isBlocked,
          status: user.isBlocked ? "blocked" : "active",
          location: "Unknown",
          storage_mb: 0,
          files: 0,
          last_active: null,
          risk: "n/a",
          joined: null,
          timezone: "UTC",
          primary_country: null,
        }));
        actualUsers = [...actualUsers, {
          is_actual: true,
          email: getEmail(),
          firstname: getEmail().split("@")[0],
          lastname: "",
          role: "",
          teams: [],
          active: true,
          status: "active",
          location: "Unknown",
          storage_mb: 0,
          files: 0,
          last_active: null,
          risk: "n/a",
          joined: null,
          timezone: "UTC",
          primary_country: null,
        }];



        // Append removed users (from logs) that aren't in the active list
        const activeEmailSet = new Set(actualUsers.map(u => u.email));
        // const removedUserEntries = (removedUserEmails || [])
        //   .filter(email => !activeEmailSet.has(email))
        //   .map(email => ({
        //     is_actual: true,
        //     email,
        //     firstname: email.split("@")[0],
        //     lastname: "",
        //     role: "",
        //     teams: [],
        //     active: false,
        //     status: "removed",
        //     location: "Unknown",
        //     storage_mb: 0,
        //     files: 0,
        //     last_active: null,
        //     risk: "n/a",
        //     joined: null,
        //     timezone: "UTC",
        //     primary_country: null,
        //   }));

        setAllUsers([...actualUsers/* , ...removedUserEntries */]);
        // console.log("removed teams names", removedTeamNames);
        // setRemovedTeams(removedTeamNames || []);
        // setRemovedUsers(removedUserEmails || []);

        // Map teams from the fetched actual teams
        // const fetchedTeams = teamNames.map((n) => {
        //   const teamName = n.getName?.() || "";
        //   const teamRoles = Array.isArray(n.getRoles?.()) ? n.getRoles() : [];
        //   // Count members from users who have this team
        //   const membersList = actualUsers.filter(u => u.teams.includes(teamName)).map(u => u.email);
        //   return {
        //     name: teamName,
        //     roles: teamRoles,
        //     members: membersList,
        //     member_count: membersList.length,
        //   };
        // });

        // //Append removed teams (from logs) that aren't in the active list
        // const activeTeamSet = new Set(fetchedTeams.map(t => t.name));
        // const removedTeamEntries = (removedTeamNames || [])
        //   .filter(name => !activeTeamSet.has(name))
        //   .map(name => ({
        //     name,
        //     roles: [],
        //     members: [],
        //     member_count: 0,
        //   }));

        // setAllTeams([...fetchedTeams, ...removedTeamEntries]);
      } catch (error) {
        console.error("Error fetching actual users and teams:", error);
      } finally {
        if (active) setLoadingUsers(false);
      }
    };
    fetchUsers();

    return () => { active = false; };
  }, []);

  // setSelectedFile lives on the files slice; we use it to seed the Files
  // dashboard with a row when the analyst clicks a file in FilesTouchedCard.
  const setSelectedFile = filesSlice.setSelectedFile;

  // Picker open state. The empty-state hero renders the picker inline (panel
  // variant, non-dismissible). Once a subject is selected, ⌘K reopens the
  // picker as a full-viewport overlay.
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const mac = navigator.platform.toUpperCase().includes("MAC");
      if ((mac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPickerOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Anchor the time-range window to the latest event timestamp seen across
  // the slice (clamped to at least Date.now()). Mock data has fixed-future
  // timestamps; without this the range filter would always return empty when
  // the real-world clock is before April 2026. With real SDK data the latest
  // event is roughly current, so the clamp is a no-op.
  const dataAnchor = useMemo(() => {
    let max = 0;
    Object.values(userActivity || {}).forEach((b) => {
      (b.events || []).forEach((e) => { if (e.timestamp > max) max = e.timestamp; });
    });
    Object.values(teamActivity || {}).forEach((b) => {
      (b.events || []).forEach((e) => { if (e.timestamp > max) max = e.timestamp; });
    });
    return Math.max(max, Date.now());
  }, [userActivity, teamActivity]);

  // Resolve the active subject. Both kinds share the same panel set; team mode
  // adds extra panels in PR4, so for PR1 we just unify on a "subject view".
  // The bundle on this view is *range-filtered*: events outside the active
  // window are dropped and aggregations are rebuilt so every panel reflects
  // the analyst's chosen scope.
  const subjectView = useMemo(() => {
    if (!selectedSubject) return null;
    const win = rangeWindow(range, dataAnchor);
    const inWindow = (e) => e.timestamp >= win.start && e.timestamp <= win.end;

    if (selectedSubject.kind === "user") {
      const user = allUsers.find((u) => u.email === selectedSubject.key);
      if (!user) return null;
      const sourceBundle = userActivity?.[user.email] || emptyBundle;
      const filtered = (sourceBundle.events || []).filter(inWindow);
      const rebuilt = buildUserBundle(filtered, user);
      // Spread original first so any non-event-derived fields (baseline,
      // unfilterable signals) survive the rebuild.
      const bundle = { ...sourceBundle, ...rebuilt };
      return {
        kind: "user",
        key: user.email,
        title: `${user.firstname} ${user.lastname}`.trim() || user.email,
        subtitle: user.email,
        meta: `${user.role} · ${user.location}`,
        initials: `${(user.firstname || user.email)[0] || ""}${(user.lastname || "")[0] || ""}`.toUpperCase() || "?",
        teams: user.teams || [],
        status: user.status || (user.active ? "active" : "blocked"),
        risk: user.risk,
        timezone: user.timezone || "UTC",
        primary_country: user.primary_country,
        bundle,
        raw: user,
      };
    }
    const team = allTeams.find((t) => t.name === selectedSubject.key);
    if (!team) return null;
    const sourceBundle = teamActivity?.[team.name] || emptyBundle;
    const filtered = (sourceBundle.events || []).filter(inWindow);
    // For teams, pass null `user` so buildUserBundle skips user-state-only
    // fields (permissionsReceived/Delta). Spread the original team bundle
    // first to preserve perMember / roleIntersection / teamShares /
    // membershipChanges which aren't event-aggregations.
    const rebuilt = buildUserBundle(filtered, null);
    const bundle = { ...sourceBundle, ...rebuilt };
    return {
      kind: "team",
      key: team.name,
      title: `#${team.name}`,
      subtitle: `${team.member_count ?? team.members?.length ?? 0} members`,
      meta: (team.roles || []).map((r) => `role: ${r}`).join(" · "),
      initials: team.name.slice(0, 2).toUpperCase(),
      teams: team.roles || [],
      status: "active",
      risk: "n/a",
      timezone: "UTC",
      primary_country: null,
      bundle,
      raw: team,
    };
  }, [selectedSubject, allUsers, allTeams, userActivity, teamActivity, range, dataAnchor]);

  // Run the anomaly engine + risk composer against the active subject's
  // range-filtered bundle. We also build a baseline anomaly run from the
  // *prior* equal-length window so the RiskScore card can show a meaningful
  // week-over-week delta.
  const anomalyView = useMemo(() => {
    if (!subjectView) return { anomalies: [], score: composeRiskScore([]) };

    const sourceBundle = subjectView.kind === "user"
      ? userActivity?.[subjectView.key] || emptyBundle
      : teamActivity?.[subjectView.key] || emptyBundle;
    const win = rangeWindow(range, dataAnchor);

    // userPriorCountries / userPriorLastActive — derive from events strictly
    // before the active range so D.2 (new country) and D.9 (dormant→active)
    // can compare current vs history.
    const priorEvents = (sourceBundle.events || []).filter((e) => e.timestamp < win.start);
    const userPriorCountries = Array.from(
      new Set(priorEvents.map((e) => e.country).filter(Boolean)),
    );
    const userPriorLastActive = priorEvents.reduce((m, e) => Math.max(m, e.timestamp), 0);

    const ctx = {
      orgDomainsSeen: knownDomains || [],
      userPriorCountries,
      userPriorLastActive,
      userPrimaryCountry: subjectView.primary_country,
    };
    const anomalies = detectAnomalies(subjectView.bundle, ctx);

    // Prior-period anomalies: rebuild a bundle for the previous equal-length
    // window and run the same rules. Used purely for the W/W delta strip.
    const winLen = win.end - win.start;
    const prevWin = { start: win.start - winLen, end: win.start };
    const prevFiltered = (sourceBundle.events || []).filter(
      (e) => e.timestamp >= prevWin.start && e.timestamp < prevWin.end,
    );
    const prevBundle = buildUserBundle(prevFiltered, subjectView.kind === "user" ? subjectView.raw : null);
    const prevAnomalies = detectAnomalies(prevBundle, {
      ...ctx,
      // Prior period uses everything-before-prior as its history baseline.
      userPriorCountries: Array.from(new Set(
        (sourceBundle.events || [])
          .filter((e) => e.timestamp < prevWin.start)
          .map((e) => e.country)
          .filter(Boolean),
      )),
    });

    return {
      anomalies,
      score: composeRiskScore(anomalies, prevAnomalies),
    };
  }, [subjectView, userActivity, teamActivity, knownDomains, range, dataAnchor]);

  // Push the resolved subject onto recent-history once per selection. Using
  // primitive key fields as the dep array prevents tight loops when the slice
  // re-renders (the subjectView object identity changes every render).
  const subjectKind = subjectView?.kind;
  const subjectKey = subjectView?.key;
  useEffect(() => {
    if (!subjectKind || !subjectKey) return;
    pushRecentSubject({ kind: subjectKind, key: subjectKey });
  }, [subjectKind, subjectKey, pushRecentSubject]);

  const handlePick = (ref) => {
    setSelectedSubject(ref);
    setPickerOpen(false);
  };

  // KPI strip is always-visible org context — rendered at the top of both the
  // empty state (where it gives picking-context) and the subject view (where
  // it stays as a frame of reference for "this user vs. the org").

  if (loadingUsers) {
    return (
      <div className="ysd-page users-page" style={{ alignItems: "center", justifyContent: "center" }}>
        <Loader message="Fetching organization data..." height="calc(100vh - 200px)" />
      </div>
    );
  }

  return (
    <div className="ysd-page users-page">
      <OrgKpiStrip users={allUsers} teams={allTeams} />

      {!subjectView ? (
        <>
          <div className="users-page__empty-hero">
            <h2>Pick a user or a team</h2>
            <p>See where, when, what, and how a subject is accessing the system — and any anomalies.</p>
          </div>
          {/* Picker zone: own positioned ancestor so the panel-variant overlay
              (`position: absolute; inset: 0`) covers only the picker area, not
              the KPI strip + hero above. */}
          <div className="users-page__picker-zone">
            <UserTeamPickerModal
              isOpen
              variant="panel"
              dismissible={false}
              users={allUsers}
              teams={allTeams}
              recentSubjects={recentSubjects || []}
              onSelectSubject={handlePick}
            />
          </div>
          {/* ⌘K viewport overlay (works even from empty state) */}
          <UserTeamPickerModal
            isOpen={pickerOpen}
            variant="viewport"
            users={allUsers}
            teams={allTeams}
            recentSubjects={recentSubjects || []}
            onClose={() => setPickerOpen(false)}
            onSelectSubject={handlePick}
          />
        </>
      ) : (
        <SubjectView
          subjectView={subjectView}
          anomalyView={anomalyView}
          selectedSubjectStack={selectedSubjectStack}
          popSubject={popSubject}
          pushSubject={pushSubject}
          palette={palette}
          allUsers={allUsers}
          allTeams={allTeams}
          removedTeams={removedTeams}
          removedUsers={removedUsers}
          recentSubjects={recentSubjects}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          handlePick={handlePick}
          setSelectedFile={setSelectedFile}
          goToDashboard={goToDashboard}
          range={range}
          dataAnchor={dataAnchor}
        />
      )}
    </div>
  );
};

// SubjectView is the post-selection panel set. Extracted so the parent's JSX
// stays readable and the empty-state / subject-view branch is obvious.
const SubjectView = ({
  subjectView,
  anomalyView,
  selectedSubjectStack,
  popSubject,
  pushSubject,
  palette,
  allUsers,
  allTeams,
  removedTeams,
  removedUsers,
  recentSubjects,
  pickerOpen,
  setPickerOpen,
  handlePick,
  setSelectedFile,
  goToDashboard,
  range,
  dataAnchor,
}) => {
  // Check if the currently selected subject has been removed
  const isRemovedUser = subjectView.kind === "user" && (removedUsers || []).includes(subjectView.key);
  const isRemovedTeam = subjectView.kind === "team" && (removedTeams || []).includes(subjectView.key);
  return (
    <>
      {/* Header */}
      <div className="users-page__head">
        <div className="users-page__head-left">
          <div className="users-page__head-avatar">{subjectView.initials}</div>
          <div className="users-page__head-body">
            <strong>{subjectView.title}</strong>
            <small>{subjectView.subtitle}</small>
            <small>{subjectView.meta}</small>
            <SubjectSnapshotLine subjectView={subjectView} />
            {(selectedSubjectStack || []).length > 1 && (
              <nav className="users-page__breadcrumb" aria-label="Subject pivot path">
                {selectedSubjectStack.map((ref, i) => (
                  <React.Fragment key={`${ref.kind}:${ref.key}:${i}`}>
                    {i > 0 && <span className="users-page__breadcrumb-sep">▸</span>}
                    <button
                      type="button"
                      className={`users-page__breadcrumb-link${i === selectedSubjectStack.length - 1 ? " is-current" : ""}`}
                      onClick={() => popSubject(i)}
                      disabled={i === selectedSubjectStack.length - 1}
                    >
                      {ref.kind === "team" ? `#${ref.key}` : ref.key}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
        </div>
        <div className="users-page__head-right">
          <div className="ysd-chips">
            {(subjectView.teams || []).map((t) => (
              <Badge key={t} tone={subjectView.kind === "team" ? "info" : "brand"} title={subjectView.kind === "team" ? t : `#${t}`}>
                {subjectView.kind === "team" ? t : `#${t}`}
              </Badge>
            ))}

            {anomalyView?.score && (
              <Badge tone={bandTone(anomalyView.score.band)} dot>
                risk {anomalyView.score.total} · {anomalyView.score.band}
              </Badge>
            )}
            {subjectView.kind === "user" && subjectView.risk && subjectView.risk !== "n/a" && !anomalyView?.score && (
              <Badge tone={riskTone(subjectView.risk)} dot>{subjectView.risk} risk</Badge>
            )}
            {subjectView.status && (
              <Badge tone={STATUS_TONE[subjectView.status] || "neutral"} dot>{subjectView.status}</Badge>
            )}
            {isRemovedUser && (
              <Badge tone="danger" dot>removed user</Badge>
            )}
            {isRemovedTeam && (
              <Badge tone="danger" dot>removed team</Badge>
            )}
          </div>
          <div className="users-page__actions">
            <button
              type="button"
              className="users-page__action-btn"
              onClick={() => setPickerOpen(true)}
              title="Find another subject (⌘K)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              Find another
            </button>
            <ExportPdfButton subjectView={subjectView} anomalyView={anomalyView} range={range} dataAnchor={dataAnchor} />
          </div>
        </div>
      </div>

      {/* Range-aware activity strip — what they did this period */}
      <SubjectActivityStrip subjectView={subjectView} range={range} dataAnchor={dataAnchor} />

      {/* Chronological story */}
      <ActivityTimelineCard subjectView={subjectView} onPushSubject={pushSubject} dataPanel="timeline" range={range} dataAnchor={dataAnchor} />

      {/* Patterns: where + when */}
      <div className="ysd-grid ysd-grid--2">
        <GeoCountriesCard subjectView={subjectView} palette={palette} dataPanel="where" range={range} dataAnchor={dataAnchor} />
        <TimeHeatmapCard subjectView={subjectView} dataPanel="when" range={range} dataAnchor={dataAnchor} />
      </div>

      {/* Volume views: op mix + storage timeline */}
      <div className="ysd-grid ysd-grid--2">
        <OpMixCard subjectView={subjectView} palette={palette} span={6} dataPanel="op_mix" range={range} dataAnchor={dataAnchor} />
        <StorageTimelineCard subjectView={subjectView} palette={palette} range={range} dataAnchor={dataAnchor} />
      </div>

      {/* Storage composition (treemap, mirrors Overview's storage distribution
          — what kinds of files this subject owns, by size or count) */}
      <SubjectStorageBreakdownCard subjectView={subjectView} palette={palette} allUsers={allUsers} range={range} dataAnchor={dataAnchor} />

      {/* Granular file access */}
      <FilesTouchedCard
        subjectView={subjectView}
        dataPanel="files_touched"
        range={range} dataAnchor={dataAnchor}
        onOpenFile={async (row) => {
          if (!row) return;
          let uuid = row.object_uuid;
          // UserFilesTouched doesn't return a uuid (only objectPath), so we
          // resolve the path against ListFilesForDashboard before pivoting.
          if (!uuid && row.path) {
            try {
              const win = rangeWindow(range, dataAnchor);
              const matches = await ListFilesForDashboard({
                startTime: win.start,
                endTime: win.end,
                query: row.path,
              });
              const exact = (matches || []).find((f) => f.path === row.path);
              uuid = exact?.uuid || matches?.[0]?.uuid;
            } catch {
              // Fall through; navigation will be skipped if uuid stays empty.
            }
          }
          if (!uuid) return;
          setSelectedFile(uuid);
          goToDashboard("files");
        }}
      />

      {/* Access flow: shares + permissions */}
      <div className="ysd-grid ysd-grid--2">
        <SharesGrantedCard subjectView={subjectView} dataPanel="shares_granted" range={range} dataAnchor={dataAnchor} />
        <PermissionsReceivedCard subjectView={subjectView} dataPanel="permissions" range={range} dataAnchor={dataAnchor} />
      </div>

      {/* What failed */}
      <DeniedCard subjectView={subjectView} dataPanel="denied" range={range} dataAnchor={dataAnchor} />

      {/* Team-only panels */}
      {subjectView.kind === "team" && (
        <>
          <div className="ysd-grid ysd-grid--2">
            <MemberListCard subjectView={subjectView} onPivotMember={pushSubject} range={range} dataAnchor={dataAnchor} />
            <RoleIntersectionCard subjectView={subjectView} range={range} dataAnchor={dataAnchor} />
          </div>
          <div className="ysd-grid ysd-grid--2">
            <TeamSharesCard subjectView={subjectView} range={range} dataAnchor={dataAnchor} />
            <MembershipChangesCard subjectView={subjectView} range={range} dataAnchor={dataAnchor} />
          </div>
        </>
      )}

      {/* Analytical summary — facts above, judgment below. Risk + anomalies
          are observations to investigate, not the headline of every subject's
          page. Putting them at the bottom keeps the dashboard from looking
          like every user is suspicious by default. */}
      <RiskScoreCard subjectView={subjectView} anomalyView={anomalyView} range={range} dataAnchor={dataAnchor} />
      <AnomaliesCard anomalyView={anomalyView} subjectView={subjectView} range={range} dataAnchor={dataAnchor} />

      {/* ⌘K picker overlay (after selection — viewport variant) */}
      <UserTeamPickerModal
        isOpen={pickerOpen}
        variant="viewport"
        users={allUsers}
        teams={allTeams}
        recentSubjects={recentSubjects || []}
        onClose={() => setPickerOpen(false)}
        onSelectSubject={handlePick}
      />
    </>
  );
};

// Shared empty-bundle placeholder so panels render zero-state instead of
// crashing when a subject has no activity yet (e.g. removed users).
const emptyBundle = {
  events: [],
  daily: [],
  hourHeatmap: Array.from({ length: 7 }, () => Array(24).fill(0)),
  opMix: [],
  countries: [],
  sessions: [],
  filesTouched: [],
  sharesGranted: [],
  deniedAttempts: [],
};

// =============================================================================
// Panels
// =============================================================================

// ── Org context bar ─────────────────────────────────────────────────────────
// Thin, always-visible org-wide stats so the analyst has frame-of-reference
// while picking or while looking at a subject. Single horizontal row with
// inline mono numbers — much lighter than a 7-card KPI grid which competed
// visually with the per-subject content below.
const OrgKpiStrip = ({ users, teams }) => {
  const stats = useMemo(() => {
    const active = users.filter((u) => u.active);
    const removed = users.filter((u) => !u.active);
    const dormantCutoff = Date.now() - 14 * 86_400_000;
    const dormant = active.filter((u) => u.last_active && Date.parse(u.last_active) < dormantCutoff);
    const highRisk = active.filter((u) => u.risk === "high");
    const externalUsers = users.filter((u) => /external|partner|guest/i.test(u.role || "")).length;

    const roleSet = new Set();
    teams.forEach((t) => (t.roles || []).forEach((r) => roleSet.add(r)));
    users.forEach((u) => { if (u.role) roleSet.add(u.role); });

    const countrySet = new Set();
    users.forEach((u) => {
      const country = (u.location || "").split(",").pop()?.trim();
      if (country) countrySet.add(country);
    });

    return {
      active: active.length,
      removed: removed.length,
      dormant: dormant.length,
      highRisk: highRisk.length,
      teams: teams.length,
      roles: roleSet.size,
      countries: countrySet.size,
      externalUsers,
    };
  }, [users, teams]);

  const items = [
    { label: "Active", value: stats.active, hint: stats.removed ? `${stats.removed} removed` : null },
    { label: "Teams", value: stats.teams },
    { label: "Roles", value: stats.roles },
    { label: "Locations", value: stats.countries, hint: "countries with activity" },
    { label: "High-risk", value: stats.highRisk, tone: stats.highRisk > 0 ? "danger" : null },
    { label: "Dormant ≥14d", value: stats.dormant, tone: stats.dormant > 0 ? "warning" : null },
    { label: "External", value: stats.externalUsers, tone: stats.externalUsers > 0 ? "warning" : null },
  ];

  return (
    <div className="users-context-bar" role="status" aria-label="Organization stats">
      <span className="users-context-bar__title">ORG</span>
      {items.map((it) => (
        <span key={it.label} className={`users-context-bar__item${it.tone ? ` users-context-bar__item--${it.tone}` : ""}`}>
          <strong>{it.value}</strong>
          <small>{it.label}</small>
        </span>
      ))}
    </div>
  );
};

// ── Activity timeline (StepNavigator) ───────────────────────────────────────
const ActivityTimelineCard = ({ subjectView, onPushSubject, dataPanel, range, dataAnchor }) => {
  const [sdkEvents, setSdkEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    const fetchPromise = subjectKind === "user"
      ? UserActivityTimeline(subjectKey, win.start, win.end)
      : TeamActivityTimeline(subjectKey, win.start, win.end);

    fetchPromise
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((row) => {
          let atTime = row.timestamp;
          if (typeof atTime === 'string') {
            atTime = new Date(atTime).getTime();
          }

          let countryStr = row.location || "Unknown";
          if (countryStr && /^\d+$/.test(String(countryStr))) {
            const n = parseInt(countryStr, 10);
            countryStr = `${(n >>> 24) & 255}.${(n >>> 16) & 255}.${(n >>> 8) & 255}.${n & 255}`;
          }

          return {
            at: Number(row.timestamp),
            op: row.operation || "Unknown",
            actor: row.operationDoneBy || row.userEmail || subjectKey,
            country: countryStr,
            detail: row.impactedUser ? `Impacted: ${row.impactedUser}` : (row.message || ""),
            success: row.success !== undefined ? row.success : true,
            permission_granted_by: row.grantedBy || row.permissionGrantedBy || "",
          };
        });
        setSdkEvents(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load activity timeline");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleEvents = useMemo(() => {
    return (subjectView.bundle.events || [])
      .filter((row) => MEANINGFUL_OPS.has(row.operation))
      .map(toStep);
  }, [subjectView.bundle.events]);

  const events = (subjectKind === "user" && sdkEvents) ? sdkEvents : bundleEvents;

  // "Follow this branch" hook: when a sharing step's recipient resolves to a
  // known subject (user or team), pivoting pushes them onto the subject stack
  // so the breadcrumb tracks ancestry. Real recipient resolution comes from
  // the SDK's share-graph in production; for PR1 we just no-op and let later
  // PRs wire the lookup.
  const handleFollow = (_step) => {
    // Placeholder — wired in PR4 when we have the share recipient → subject
    // resolution. Leaving the prop in place so the navigator's "Follow"
    // affordance is still rendered for forking ops.
    if (typeof onPushSubject !== "function") return;
  };

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Activity timeline" subtitle="Loading timeline...">
        <Loader message="Loading timeline data…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Activity timeline" subtitle="Error loading data">
        <ErrorHint message={error} />
      </ChartCard>
    );
  }

  if (!events.length) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Activity timeline" subtitle="Meaningful actions on this subject">
        <EmptyHint>No meaningful events recorded for this subject in the active range.</EmptyHint>
      </ChartCard>
    );
  }


  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Activity timeline"
      subtitle={`${events.length} meaningful event${events.length === 1 ? "" : "s"} — shares, role changes, deletes, writes`}
    >
      <StepNavigator events={events} onFollow={handleFollow} />
    </ChartCard>
  );
};

// ── Where (geo) — horizontal country bar with trust-tier colours ────────────
const GeoCountriesCard = ({ subjectView, palette, dataPanel, range, dataAnchor }) => {
  const bundleCountries = subjectView.bundle.countries || [];
  const [sdkCountries, setSdkCountries] = useState(null);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [errorGeo, setErrorGeo] = useState(null);

  // Fetch from SDK when subject is a user
  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;
  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkCountries(null);
      return;
    }
    let active = true;
    setLoadingGeo(true);
    setErrorGeo(null);
    const win = rangeWindow(range, dataAnchor);
    UserCountriesAccessedFrom(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((item) => ({
          country: item.location || "Unknown",
          count: item.count || 0,
          firstSeen: win.start,
          lastSeen: win.end,
          trustTier: "allowed",
          newThisRange: false,
        }));
        setSdkCountries(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setErrorGeo("Failed to load geographical data");
      })
      .finally(() => {
        if (active) setLoadingGeo(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  // Use SDK data for users if available, else fall back to bundle
  const countries = (subjectKind === "user" && sdkCountries) ? sdkCountries : bundleCountries;

  const option = useMemo(() => {
    if (!countries.length) return null;
    const ordered = [...countries].sort((a, b) => a.count - b.count); // ascending → biggest at top after y-flip
    const tierColor = (c) =>
      c.trustTier === "blocked" ? (palette.danger || "#ef4444") :
        c.trustTier === "trusted" ? (palette.success || "#10b981") :
          (palette.warning || "#f59e0b");
    return {
      backgroundColor: "transparent",
      grid: { top: 10, right: 56, bottom: 36, left: 160 },
      tooltip: {
        trigger: "axis",
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          const row = ordered[p.dataIndex];
          if (!row) return '';
          return `<b>${row.country}</b><br/>${row.count} events`;
        },
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: ordered.map((r) => r.country),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.text, fontSize: 11, width: 140, overflow: "truncate", ellipsis: "..." },
      },
      series: [{
        type: "bar",
        data: ordered.map((r) => ({
          value: r.count,
          itemStyle: { color: tierColor(r), borderRadius: [0, 6, 6, 0] },
        })),
        label: { show: true, position: "right", color: palette.text, fontSize: 11 },
      }],
    };
  }, [countries, palette]);

  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Where"
      subtitle="Countries this subject accessed from"
      span={6}
    >
      {loadingGeo ? <Loader message="Loading geo data…" height="300px" /> :
        errorGeo ? <ErrorHint message={errorGeo} /> :
          option ? <EChart option={option} height={300} /> : <EmptyHint>No geo data for this subject yet.</EmptyHint>}
    </ChartCard>
  );
};

// ── When (heatmap) — 7×24 day-of-week × hour-of-day grid ────────────────────
// We render the heatmap as a CSS grid (no ECharts dependency for one panel)
// so we can colour cells via CSS variables and keep tooltips light.
const TimeHeatmapCard = ({ subjectView, dataPanel, range, dataAnchor }) => {
  const [sdkAll, setSdkAll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkAll(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserTimeHeatmap(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
        (items || []).forEach(({ dow, hour, count }) => {
          if (dow >= 0 && dow <= 6 && hour >= 0 && hour <= 23) {
            grid[dow][hour] += count;
          }
        });
        setSdkAll(grid);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load heatmap data");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const grid = sdkAll || Array.from({ length: 7 }, () => Array(24).fill(0));
  const max = useMemo(() => grid.reduce((m, row) => Math.max(m, ...row), 0), [grid]);
  const total = useMemo(() => grid.reduce((s, row) => s + row.reduce((a, n) => a + n, 0), 0), [grid]);

  // Off-hours (UTC-bucketed for PR1; G.1 in the plan describes the future
  // tz-aware version). Off-hours = before 8am or 8pm and later.
  const offHours = useMemo(() => {
    let off = 0;
    grid.forEach((row) => row.forEach((n, h) => { if (h < 8 || h >= 20) off += n; }));
    return off;
  }, [grid]);

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="When" subtitle="Hour-of-day × day-of-week activity" span={6}>
        <Loader message="Loading heatmap…" height="300px" />
      </ChartCard>
    );
  }

  if (!total) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="When" subtitle="Hour-of-day × day-of-week activity" span={6}>
        <EmptyHint>No timing data for this subject yet.</EmptyHint>
      </ChartCard>
    );
  }

  const offPct = total ? ((offHours / total) * 100).toFixed(1) : "0.0";
  const cellColor = (n) => {
    if (!n) return undefined;
    const ratio = max ? n / max : 0;
    // Use the brand purple at varying alpha so dense cells feel like bursts
    // of activity without forcing a separate dark/light palette.
    return { background: `rgba(130, 55, 187, ${0.18 + ratio * 0.72})` };
  };

  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const HOURS = Array.from({ length: 24 }, (_, h) => h);

  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="When"
      subtitle={`Off-hours: ${offHours} / ${total} (${offPct}%) · before 8am or after 8pm (${subjectView.timezone})`}
      span={6}
    >
      {loading ? (
        <Loader message="Loading heatmap…" minHeight={300} />
      ) : error ? (
        <ErrorHint message={error} />
      ) : (
        <div className="users-heatmap" role="img" aria-label="Activity heatmap">
          <span className="users-heatmap__hour-axis" />
          {HOURS.map((h) => (
            <span key={`hh-${h}`} className="users-heatmap__hour-axis">
              {h % 6 === 0 ? h : ""}
            </span>
          ))}
          {grid.map((row, dow) => (
            <React.Fragment key={`d-${dow}`}>
              <span className="users-heatmap__day-label">{DAY_LABELS[dow]}</span>
              {row.map((n, h) => (
                <div
                  key={`c-${dow}-${h}`}
                  className="users-heatmap__cell"
                  style={cellColor(n)}
                  title={`${DAY_LABELS[dow]} ${h.toString().padStart(2, "0")}:00 — ${n} event${n === 1 ? "" : "s"}`}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </ChartCard>
  );

};

// ── What (op mix) — donut by category ───────────────────────────────────────
const OpMixCard = ({ subjectView, palette, span, dataPanel, range, dataAnchor }) => {
  const bundleOpMix = subjectView.bundle.opMix || [];
  const [sdkOpMix, setSdkOpMix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkOpMix(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserOpCount(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((item) => ({
          operation: item.operation || item.op || "Unknown",
          count: item.count || 0,
        }));
        setSdkOpMix(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load operation mix");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const opMix = (subjectKind === "user" && sdkOpMix) ? sdkOpMix : bundleOpMix;


  const option = useMemo(() => {
    if (!opMix.length) return null;
    const byClass = {};
    opMix.forEach(({ operation, count }) => {
      const cls = OP_TO_CLASS[operation] || "other";
      byClass[cls] = (byClass[cls] || 0) + count;
    });
    const data = Object.entries(byClass)
      .map(([cls, count]) => ({
        name: cls,
        value: count,
        itemStyle: { color: OP_CLASS_COLORS[cls] || "#cbd5e1" },
      }));
    const total = data.reduce((a, d) => a + d.value, 0);
    return {
      backgroundColor: "transparent",
      tooltip: {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (p) => `<b>${p.name}</b><br/>${p.value} ops · ${total ? ((p.value / total) * 100).toFixed(1) : 0}%`,
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        bottom: 0,
        left: "center",
        textStyle: { color: palette.text, fontSize: 11 },
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 14,
      },
      series: [{
        type: "pie",
        radius: ["48%", "70%"],
        center: ["50%", "44%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: palette.cardBg, borderWidth: 2, borderRadius: 4 },
        label: { show: false },
        labelLine: { show: false },
        data,
      }],
    };
  }, [opMix, palette]);

  // Surface the "last action" timestamp in the subtitle so the analyst doesn't
  // have to scroll back to the timeline to confirm freshness.
  const last = subjectView.bundle.events.at(-1)?.timestamp;
  const subtitle = `Operations broken down by class${last ? ` · last action ${relativeTime(last)}` : ""}`;

  return (
    <ChartCard data-anomaly-panel={dataPanel} title="What" subtitle={subtitle} span={span}>
      {loading ? <Loader message="Loading op mix data…" height="280px" /> :
        option ? <EChart option={option} height={280} /> : <EmptyHint>No operations recorded for this subject.</EmptyHint>}
    </ChartCard>
  );
};

// ── Files & folders touched ─────────────────────────────────────────────────
// Sortable table over bundle.filesTouched. Sorted by bytes_out descending by
// default to surface exfiltration candidates. Rows with confidential / pii
// tags get a red flag. Click on a row → host wires a callback that selects
// the file and switches to the Files dashboard.
const TOUCHED_COLUMNS = [
  { key: "path", label: "Object", align: "left" },
  { key: "reads", label: "Reads", align: "right" },
  { key: "writes", label: "Writes", align: "right" },
  { key: "shares", label: "Shares", align: "right" },
  { key: "deletes", label: "Deletes", align: "right" },
  { key: "bytes_out", label: "Bytes out", align: "right" },
  { key: "bytes_in", label: "Bytes in", align: "right" },
  { key: "last_touched", label: "Last touched", align: "right" },
];
const FLAG_TAGS = new Set(["confidential", "pii", "secret"]);

const FilesTouchedCard = ({ subjectView, onOpenFile, dataPanel, range, dataAnchor }) => {
  const [sdkRows, setSdkRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkRows(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserFilesTouched(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((r) => ({
          path: r.objectPath || r.object_path || r.path || "Unknown",
          object_uuid: r.objectUuid || r.object_uuid || "",
          file_type: r.fileType || r.file_type || "other",
          reads: Number(r.reads || 0),
          writes: Number(r.writes || 0),
          shares: Number(r.shares || 0),
          deletes: Number(r.deletes || 0),
          bytes_in: Number(r.bytesIn ?? r.bytes_in ?? 0),
          bytes_out: Number(r.bytesOut ?? r.bytes_out ?? 0),
          last_touched: Number(r.lastTouched ?? r.last_touched ?? 0),
          tags: r.tags || [],
        }));
        setSdkRows(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load file activity");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleRows = subjectView.bundle.filesTouched || [];
  const rows = (subjectKind === "user" && sdkRows) ? sdkRows : bundleRows;

  const [sortKey, setSortKey] = useState("bytes_out");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Numeric sort if both numbers, otherwise string compare. Stable enough
      // for a single-column click-to-sort affordance.
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const cmp = String(av || "").localeCompare(String(bv || ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "path" ? "asc" : "desc"); }
  };

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Files & folders touched" subtitle="Loading file activity...">
        <Loader message="Loading files data…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Files & folders touched" subtitle="Error loading data">
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  if (!rows.length) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Files & folders touched" subtitle="Per-object reads, writes, shares, deletes">
        <EmptyHint>No file activity recorded for this subject in the active range.</EmptyHint>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Files & folders touched"
      subtitle={`${rows.length} object${rows.length === 1 ? "" : "s"} · sort by clicking a header · click a row to open in Files`}
    >
      <div className="ysd-table__scroll">
        <table className="ysd-table">
          <thead>
            <tr>
              {TOUCHED_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`sortable ${sortKey === col.key ? "sort-active" : ""}`}
                  onClick={() => setSort(col.key)}
                  style={{ textAlign: col.align, cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  {col.label}
                  <span className="sort-arrow">{sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
                </th>
              ))}
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const flagged = (r.tags || []).some((t) => FLAG_TAGS.has(t));
              const clickable = !!(r.object_uuid || r.path);
              return (
                <tr
                  key={r.object_uuid || r.path}
                  className={`${flagged ? "ysd-row--danger" : ""} ${clickable ? "ysd-row--file" : ""}`}
                  onClick={clickable ? () => onOpenFile(r) : undefined}
                  style={clickable ? { cursor: "pointer" } : undefined}
                  title={clickable ? "Open in Files dashboard" : undefined}
                >
                  <td>
                    <div className="ysd-name-cell">
                      <span className="ysd-name-cell__icon">{ICON_FOR_TYPE[r.file_type] || "📄"}</span>
                      <span className="ysd-mono">{r.path}</span>
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>{r.reads.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{r.writes.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{r.shares.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{r.deletes.toLocaleString()}</td>
                  <td style={{ textAlign: "right" }}>{r.bytes_out ? humanBytes(r.bytes_out) : "—"}</td>
                  <td style={{ textAlign: "right" }}>{r.bytes_in ? humanBytes(r.bytes_in) : "—"}</td>
                  <td style={{ textAlign: "right" }}><span className="ysd-mono">{relativeTime(r.last_touched)}</span></td>
                  <td>
                    <div className="ysd-chips">
                      {(r.tags || []).map((t) => (
                        <Badge key={t} tone={FLAG_TAGS.has(t) ? "danger" : "neutral"} dot={FLAG_TAGS.has(t)}>
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

const ICON_FOR_TYPE = {
  folder: "📁", doc: "📝", sheet: "📊", pdf: "📕",
  image: "🖼", video: "🎬", audio: "🔊", csv: "📋",
  figma: "🎨", archive: "📦",
};

// ── Sharing actions ─────────────────────────────────────────────────────────
// Filter pill on top: All / Internal / Team / External.
// External recipients flagged red with a danger badge.
const SHARE_FILTERS = [
  { v: "all", l: "All" },
  { v: "user", l: "Internal" },
  { v: "team", l: "Team" },
  { v: "external", l: "External" },
];

const _grantedByText = (g) => {
  if (!g) return "—";
  if (g.is_self) return "self";
  if (Array.isArray(g.teams) && g.teams.length) return `team: ${g.teams.join(", ")}`;
  if (Array.isArray(g.roles) && g.roles.length) return `role: ${g.roles.join(", ")}`;
  return "—";
};

const SharesGrantedCard = ({ subjectView, dataPanel, range, dataAnchor }) => {
  const [sdkAll, setSdkAll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkAll(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserSharingActions(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((s) => ({
          at: s.timestamp || 0,
          object_path: s.object_path || "Unknown",
          recipient: s.recipient || "Unknown",
          external_domain: s.external_domain || "",
          recipient_type: s.recipient_type || "user",
          granted_by: { is_self: s.granted_by === "self" || s.granted_by === subjectKey, teams: [], roles: [] },
          success: s.success !== false,
        }));
        setSdkAll(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load sharing actions");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleAll = subjectView.bundle.sharesGranted || [];
  const all = (subjectKind === "user" && sdkAll) ? sdkAll : bundleAll;

  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    if (filter === "all") return all;
    return all.filter((s) => s.recipient_type === filter);
  }, [all, filter]);

  const right = (
    <div className="ysd-toggle">
      {SHARE_FILTERS.map((f) => (
        <button
          key={f.v}
          className={filter === f.v ? "active" : ""}
          onClick={() => setFilter(f.v)}
          type="button"
        >
          {f.l}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Sharing actions" subtitle="Loading sharing actions..." right={right} span={6}>
        <Loader message="Loading shares data…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Sharing actions" subtitle="Error loading data" right={right} span={6}>
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  if (!all.length) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Sharing actions" subtitle="Shares granted by this subject" right={right} span={6}>
        <EmptyHint>No share actions recorded.</EmptyHint>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Sharing actions"
      subtitle={`${rows.length} of ${all.length} share${all.length === 1 ? "" : "s"} · external in red`}
      right={right}
      span={6}
    >
      <div className="ysd-table__scroll" style={{ maxHeight: 360 }}>
        <table className="ysd-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Object</th>
              <th>Recipient</th>
              <th>Type</th>
              <th>Granted by</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={i} className={s.recipient_type === "external" ? "ysd-row--danger" : ""}>
                <td><span className="ysd-mono">{relativeTime(s.at)}</span></td>
                <td><span className="ysd-mono">{s.object_path}</span></td>
                <td>
                  <span style={{ fontWeight: 600 }}>{s.recipient}</span>
                  {s.external_domain && (
                    <small style={{ display: "block", color: "var(--text-grey-weak)" }}>{s.external_domain}</small>
                  )}
                </td>
                <td>
                  <Badge tone={s.recipient_type === "external" ? "danger" : s.recipient_type === "team" ? "info" : "neutral"} dot>
                    {s.recipient_type}
                  </Badge>
                </td>
                <td style={{ fontSize: 11.5 }}>{_grantedByText(s.granted_by)}</td>
                <td>
                  <Badge tone={s.success === false ? "danger" : "success"} dot>
                    {s.success === false ? "denied" : "ok"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

// ── Permissions received ────────────────────────────────────────────────────
// Two columns: current roles + current teams. A diff strip below each lists
// added / removed entries in the active range. Newly-added admin/exec roles
// (or teams whose roles include "admin"/"exec") render as danger badges to
// flag escalation.
const HIGH_PRIV_TOKENS = /admin|exec|sec(urity)?-(admin|lead)/i;

const PermissionsReceivedCard = ({ subjectView, dataPanel, range, dataAnchor }) => {
  const [sdkEdges, setSdkEdges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkEdges(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserPermissionRecieved(subjectKey, win.start, win.end)
      .then((result) => {
        if (!active) return;
        // SDK returns { roles: string[], teams: string[] } directly — not an array.
        const roles = (result?.roles || []).map((r) => ({ role: r, team: "" }));
        const teams = (result?.teams || []).map((t) => ({ role: "", team: t }));
        setSdkEdges([...roles, ...teams]);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load permissions");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleEdges = subjectView.bundle.permissionsReceived || [];
  const edges = (subjectKind === "user" && sdkEdges) ? sdkEdges : bundleEdges;
  const delta = subjectView.bundle.permissionsDelta || { added: [], removed: [] };

  if (subjectView.kind === "team") {
    return (
      <ChartCard
        data-anomaly-panel={dataPanel}
        title="Roles granted to team"
        subtitle="What this team's members inherit"
        span={6}
      >
        <div className="ysd-chips">
          {(subjectView.raw.roles || []).map((r) => (
            <Badge key={r} tone={HIGH_PRIV_TOKENS.test(r) ? "danger" : "info"} dot={HIGH_PRIV_TOKENS.test(r)} title={r}>
              {r}
            </Badge>
          ))}
          {(subjectView.raw.roles || []).length === 0 && <EmptyHint>No roles granted to this team.</EmptyHint>}
        </div>

      </ChartCard>
    );
  }

  const roles = edges.filter((e) => e.role).map((e) => e.role);
  const teams = edges.filter((e) => e.team).map((e) => e.team);

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Permissions received" subtitle="Loading permissions data..." span={6}>
        <Loader message="Loading permissions…" minHeight={100} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Permissions received" subtitle="Error loading data" span={6}>
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Permissions received"
      subtitle="Where this user's authority comes from right now"
      span={6}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <PermissionColumn
          title="Roles"
          items={roles}
          delta={delta}
          flavour="role"
        />
        <PermissionColumn
          title="Teams"
          items={teams}
          delta={delta}
          flavour="team"
        />
      </div>
    </ChartCard>
  );
};

const PermissionColumn = ({ title, items, delta, flavour }) => {
  const added = (delta.added || []).filter((e) => e[flavour]).map((e) => e[flavour]);
  const removed = (delta.removed || []).filter((e) => e[flavour]).map((e) => e[flavour]);
  return (
    <div>
      <div className="files-page__folder-contents-title">{title}</div>
      <div className="ysd-chips" style={{ marginBottom: 8 }}>
        {items.length === 0 && <EmptyHint>None.</EmptyHint>}
        {items.map((it) => {
          const escalated = added.includes(it) && HIGH_PRIV_TOKENS.test(it);
          const label = flavour === "team" ? `#${it}` : it;
          return (
            <Badge
              key={it}
              tone={escalated ? "danger" : "info"}
              dot={escalated}
              title={label}
            >
              {label}
              {escalated && <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.85 }}>NEW · ESCALATION</span>}
            </Badge>
          );
        })}
      </div>

      {(added.length > 0 || removed.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11 }}>
          {added.length > 0 && (
            <div style={{ color: "var(--accent-colors-green)" }}>
              + {added.join(", ")} <small style={{ color: "var(--text-grey-weak)" }}>added in range</small>
            </div>
          )}
          {removed.length > 0 && (
            <div style={{ color: "var(--accent-colors-red)" }}>
              − {removed.join(", ")} <small style={{ color: "var(--text-grey-weak)" }}>removed in range</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Denied attempts ─────────────────────────────────────────────────────────
// Failed audit rows. (A "followed by allow" bypass column used to live here,
// but it depended on correlating denials against allowed events by object_uuid
// — the activity-timeline SDK doesn't return an object id, so the join could
// never match. Dropped until we have a denial-with-target endpoint.)

const DeniedCard = ({ subjectView, dataPanel, range, dataAnchor }) => {
  const [sdkDenied, setSdkDenied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkDenied(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserDeniedAttempts(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((e) => ({
          timestamp: Number(e.timestamp ?? 0),
          user_email: subjectKey,
          operation: e.operation || "Unknown",
          object_uuid: e.objectPath || "",
          object_path: e.objectPath || "",
          country: e.ipAddress ? "IP: " + e.ipAddress : "Unknown",
          message: e.message || "Access denied",
        }));
        setSdkDenied(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load denied attempts");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);

  const bundleDenied = subjectView.bundle.deniedAttempts || [];
  const denied = (subjectKind === "user" && sdkDenied) ? sdkDenied : bundleDenied;
  const rows = denied;

  if (loading) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Denied attempts" subtitle="Loading denied attempts...">
        <Loader message="Loading denied attempts…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Denied attempts" subtitle="Error loading data">
        <ErrorHint message={error} />
      </ChartCard>
    );
  }

  if (!rows.length) {
    return (
      <ChartCard data-anomaly-panel={dataPanel} title="Denied attempts" subtitle="Permission-blocked attempts">
        <EmptyHint>No denied attempts recorded in this range.</EmptyHint>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      data-anomaly-panel={dataPanel}
      title="Denied attempts"
      subtitle={`${denied.length} denial${denied.length === 1 ? "" : "s"}`}
    >
      <div className="ysd-table__scroll">
        <table className="ysd-table">
          <thead>
            <tr>
              <th>When</th>
              {subjectView.kind === "team" && <th>Actor</th>}
              <th>Op</th>
              <th>Object</th>
              <th>Country</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const path =
                r.object_path ||
                r.subject?.share?.acl?.object_path ||
                r.subject?.removeVersion?.file_path ||
                Object.values(r.subject || {})[0]?.path_of_object ||
                "—";
              return (
                <tr key={i}>
                  <td><span className="ysd-mono">{relativeTime(r.timestamp)}</span></td>
                  {subjectView.kind === "team" && <td>{r.user_email}</td>}
                  <td><Badge tone="brand">{r.operation}</Badge></td>
                  <td><span className="ysd-mono">{path}</span></td>
                  <td>{r.country || "—"}</td>
                  <td style={{ fontSize: 11.5 }}>{r.message || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

// ── Risk score panel ────────────────────────────────────────────────────────
// Big composite number, band label, week-over-week delta strip, and a row of
// signal badges (one per anomaly contributing to the score). Sparkline is
// derived from the bundle's daily activity series — a quick visual proxy for
// "what was this user doing all week" alongside the numeric score.
const RiskScoreCard = ({ subjectView, anomalyView, range, dataAnchor }) => {
  const [sdkScore, setSdkScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView?.kind;
  const subjectKey = subjectView?.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkScore(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserRiskScore(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        let total = 0;
        const signals = [];
        (items || []).forEach((item) => {
          const kind = item.signal || "unknown";
          let weight = 0;
          let label = "Unknown anomaly";

          if (kind === "total_events") { weight = 0; label = "High event volume"; }
          else if (kind === "failed_operations") { weight = 20; label = "Failed operations spike"; }
          else if (kind === "off_hours_access") { weight = 15; label = "Off-hours access"; }
          else if (kind === "mass_download") { weight = 30; label = "Mass download"; }
          else if (kind === "distinct_locations") { weight = 10; label = "Multiple login locations"; }

          if (weight > 0) {
            total += weight;
            signals.push({ kind, label, weight });
          }
        });
        const scoreObj = {
          total: Math.min(100, total),
          band: total > 60 ? "high" : total > 30 ? "medium" : "low",
          weekOverWeek: { deltaPct: 0, previous: 0 },
          signals: signals.sort((a, b) => b.weight - a.weight),
        };
        setSdkScore(scoreObj);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to calculate risk score");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);

  const score = (subjectKind === "user" && sdkScore) ? sdkScore : anomalyView?.score;
  const totalEvents = subjectView.bundle?.events?.length || 0;

  if (!score) return null;



  const tone = bandTone(score.band);
  const wow = score.weekOverWeek || { deltaPct: 0, previous: 0 };
  const direction = wow.deltaPct === 0 ? "flat" : wow.deltaPct > 0 ? "up" : "down";

  // Show top 5 signals as a clean ordered list. The Anomalies feed below has
  // the comprehensive view with descriptions + investigate links, so this
  // panel just needs the headline contributions.
  const topSignals = score.signals.slice(0, 5);
  const remaining = Math.max(0, score.signals.length - topSignals.length);
  const signalTone = (w) => (w >= 25 ? "danger" : w >= 15 ? "warning" : "info");

  return (
    <ChartCard
      title="Risk score"
      subtitle={`${score.signals.length} contributing signal${score.signals.length === 1 ? "" : "s"} · sample size ${totalEvents} event${totalEvents === 1 ? "" : "s"}`}
    >
      <div className="users-risk">
        <div className={`users-risk__hero users-risk__hero--${tone}`}>
          <div className="users-risk__total-block">
            <span className={`users-risk__total users-risk__total--${tone}`}>{score.total}</span>
            <span className="users-risk__total-suffix">/ 100</span>
          </div>
          <span className={`users-risk__band users-risk__band--${tone}`}>{score.band}</span>
          <div className={`users-risk__delta users-risk__delta--${direction}`}>
            <span className="users-risk__delta-arrow">
              {direction === "up" ? "▲" : direction === "down" ? "▼" : "—"}
            </span>
            <span className="users-risk__delta-text">
              {Math.abs(wow.deltaPct)}% vs prior period
              <small>was {wow.previous}</small>
            </span>
          </div>
        </div>

        <div className="users-risk__signals-block">
          <div className="users-risk__signals-title">
            Top contributing signals
          </div>
          {topSignals.length === 0 ? (
            <EmptyHint>No anomalies — clean window. ✓</EmptyHint>
          ) : (
            <ul className="users-risk__signals-list">
              {topSignals.map((s) => {
                const t = signalTone(s.weight);
                return (
                  <li key={s.kind + s.label} className={`users-risk__signal users-risk__signal--${t}`}>
                    <span className={`users-risk__signal-weight users-risk__signal-weight--${t}`}>+{s.weight}</span>
                    <span className="users-risk__signal-label">{s.label}</span>
                  </li>
                );
              })}
              {remaining > 0 && (
                <li className="users-risk__signal users-risk__signal--more">
                  <span className="users-risk__signal-weight users-risk__signal-weight--more">+{remaining}</span>
                  <span className="users-risk__signal-label">more — see Anomalies below</span>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </ChartCard>
  );
};

// ── Anomalies feed ──────────────────────────────────────────────────────────
// Severity-sorted list. Each row renders a severity dot, a one-line title +
// description, and an "investigate →" affordance that anchor-jumps the page
// to the panel that evidences the anomaly. Panel anchors are rendered as
// invisible spans on the relevant cards so the URL fragment doesn't change
// (see PANEL_ANCHOR_ID below).
const SEVERITY_TONE = { high: "danger", medium: "warning", low: "info" };

const AnomaliesCard = ({ anomalyView, subjectView, range, dataAnchor }) => {
  const [sdkAnomalies, setSdkAnomalies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView?.kind;
  const subjectKey = subjectView?.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkAnomalies(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserAnomalies(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const ANOMALY_META = {
          bulk_download: { title: "Bulk download", severity: "high", weight: 35 },
          bulk_delete: { title: "Bulk delete", severity: "high", weight: 40 },
          external_share_spike: { title: "External share spike", severity: "medium", weight: 25 },
        };
        const mapped = (items || []).map((item, idx) => {
          const type = item.anomalyType || item.anomaly_type || "unknown";
          const startMs = Number(item.startTime ?? item.start_time ?? 0);
          const endMs = Number(item.endTime ?? item.end_time ?? 0);
          const count = Number(item.count ?? 0);
          const meta = ANOMALY_META[type] || { title: type, severity: "medium", weight: 15 };
          const fmtMs = (ms) => ms ? new Date(ms).toLocaleDateString() : "?";
          return {
            id: `anom_${type}_${idx}`,
            severity: meta.severity,
            title: meta.title,
            description: `${count.toLocaleString()} events · ${fmtMs(startMs)} → ${fmtMs(endMs)}`,
            weight: meta.weight,
            evidence: { panel: type === "bulk_delete" ? "files" : type === "external_share_spike" ? "shares" : "files", event_ids: [] },
          };
        });

        setSdkAnomalies(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load anomalies");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const anomalies = (subjectKind === "user" && sdkAnomalies) ? sdkAnomalies : (anomalyView?.anomalies || []);

  if (loading) {
    return (
      <ChartCard title="Anomalies" subtitle="Scanning for suspicious patterns...">
        <Loader message="Analyzing behavior…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard title="Anomalies" subtitle="Error scanning anomalies">
        <ErrorHint message={error} />
      </ChartCard>
    );
  }

  if (!anomalies.length) {
    return (
      <ChartCard title="Anomalies" subtitle="Detected suspicious patterns">
        <EmptyHint>No anomalies detected in this range.</EmptyHint>
      </ChartCard>
    );
  }


  const investigate = (panel) => {
    // Soft scroll into view + brief outline pulse on the target card. We
    // attach a temporary class via DOM (cheaper than re-rendering everything
    // through state) and remove it after the animation runs.
    const el = document.querySelector(`[data-anomaly-panel="${panel}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("users-investigate-flash");
    setTimeout(() => el.classList.remove("users-investigate-flash"), 1600);
  };

  return (
    <ChartCard
      title="Anomalies"
      subtitle={`${anomalies.length} signal${anomalies.length === 1 ? "" : "s"} sorted by impact`}
    >
      <div className="users-anomalies">
        {anomalies.map((a) => (
          <div key={a.id} className={`users-anomalies__row users-anomalies__row--${a.severity}`}>
            <span className={`users-anomalies__dot users-anomalies__dot--${a.severity}`} />
            <div className="users-anomalies__body">
              <strong>{a.title}</strong>
              <small>{a.description}</small>
              <small className="users-anomalies__meta">
                Severity: {a.severity} · Weight: +{Math.round(a.weight)} · Evidence: {a.evidence.event_ids.length} event{a.evidence.event_ids.length === 1 ? "" : "s"}
              </small>
            </div>
            <button
              type="button"
              className="users-anomalies__cta"
              onClick={() => investigate(a.evidence.panel)}
            >
              <Badge tone={SEVERITY_TONE[a.severity] || "neutral"}>investigate →</Badge>
            </button>
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

// ── Team-only panels ────────────────────────────────────────────────────────
// Rendered only when subjectView.kind === "team". Member rows pivot into the
// individual user view via pushSubject({kind:"user"}), preserving the team
// in the breadcrumb so the analyst can step back.

const MemberListCard = ({ subjectView, onPivotMember, range, dataAnchor }) => {
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (subjectView.kind !== "team") return;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    TeamMembers(subjectView.key, win.start, win.end)
      .then((items) => {
        if (!active) return;
        setMembers(items || []);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load members");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectView.key, subjectView.kind, range, dataAnchor]);


  if (loading) {
    return (
      <ChartCard title="Members" subtitle="People in this team" span={6}>
        <Loader message="Loading members…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard title="Members" subtitle="Error loading members" span={6}>
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  if (!members || !members.length) {
    return (
      <ChartCard title="Members" subtitle="People in this team" span={6}>
        <EmptyHint>No members listed for this team.</EmptyHint>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Members"
      subtitle={`${members.length} member${members.length === 1 ? "" : "s"} · click a row to pivot to that user`}
      span={6}
    >
      <div className="ysd-table__scroll" style={{ maxHeight: 320 }}>
        <table className="ysd-table" aria-label="Team member list">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => (
              <tr
                key={i}
                className="ysd-row--file"
                style={{ cursor: "pointer" }}
                onClick={() => onPivotMember({ kind: "user", key: m.email })}
                title="Open this member in user view"
              >
                <td><span className="ysd-mono">{m.email}</span></td>
                <td>{m.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

const RoleIntersectionCard = ({ subjectView, range, dataAnchor }) => {
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (subjectView.kind !== "team") return;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    TeamRoleIntersection(subjectView.key, win.start, win.end)
      .then((items) => {
        if (!active) return;
        setRoles(items || []);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load role intersection");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectView.key, subjectView.kind, range, dataAnchor]);


  if (loading) {
    return (
      <ChartCard title="Role intersection" subtitle="What every member has vs what only some have" span={6}>
        <Loader message="Loading roles…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard title="Role intersection" subtitle="Error loading roles" span={6}>
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  const maxCount = roles && roles.length ? Math.max(...roles.map(r => r.memberCount)) : 0;
  const all = (roles || []).filter(r => r.memberCount === maxCount).map(r => r.role);
  const partial = (roles || []).filter(r => r.memberCount < maxCount).map(r => r.role);

  return (
    <ChartCard
      title="Role intersection"
      subtitle="What every member has vs what only some have"
      span={6}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div className="files-page__folder-contents-title">Most common roles</div>
          {all.length ? (
            <div className="ysd-chips">
              {all.map((r) => <Badge key={r} tone="info" title={r}>{r}</Badge>)}

            </div>
          ) : <EmptyHint>No common roles.</EmptyHint>}
        </div>
        <div>
          <div className="files-page__folder-contents-title">Other roles</div>
          {partial.length ? (
            <div className="ysd-chips">
              {partial.map((r) => <Badge key={r} tone="warning" dot title={r}>{r}</Badge>)}

            </div>
          ) : <EmptyHint>No outliers — every role is held by everyone.</EmptyHint>}
        </div>
      </div>
    </ChartCard>
  );
};

const TeamSharesCard = ({ subjectView }) => {
  const rows = subjectView.bundle.teamShares || [];
  if (!rows.length) {
    return (
      <ChartCard title="Team-scoped shares" subtitle="Shares to or from this team" span={6}>
        <EmptyHint>No team-scoped sharing in this range.</EmptyHint>
      </ChartCard>
    );
  }
  return (
    <ChartCard
      title="Team-scoped shares"
      subtitle={`${rows.length} share${rows.length === 1 ? "" : "s"} touching this team`}
      span={6}
    >
      <div className="ysd-table__scroll" style={{ maxHeight: 320 }}>
        <table className="ysd-table" aria-label="Team shares">
          <thead>
            <tr>
              <th>When</th>
              <th>Member</th>
              <th>Object</th>
              <th>Recipient</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={i} className={s.recipient_type === "external" ? "ysd-row--danger" : ""}>
                <td><span className="ysd-mono">{relativeTime(s.at)}</span></td>
                <td>{s.member}</td>
                <td><span className="ysd-mono">{s.object_path}</span></td>
                <td>{s.recipient}</td>
                <td>
                  <Badge tone={s.recipient_type === "external" ? "danger" : s.recipient_type === "team" ? "info" : "neutral"} dot>
                    {s.recipient_type}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

const MembershipChangesCard = ({ subjectView, range, dataAnchor }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (subjectView.kind !== "team") return;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    TeamMembershipChanges(subjectView.key, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map(r => ({
          at: r.timestamp || 0,
          op: r.operation || "Unknown",
          target_email: r.user_email || "Unknown",
          actor: r.granted_by || "Unknown"
        }));
        setRows(mapped);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load membership changes");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectView.key, subjectView.kind, range, dataAnchor]);


  if (loading) {
    return (
      <ChartCard title="Membership changes" subtitle="Members added / removed in range" span={6}>
        <Loader message="Loading membership changes…" minHeight={200} />
      </ChartCard>
    );
  }

  if (error) {
    return (
      <ChartCard title="Membership changes" subtitle="Error loading changes" span={6}>
        <ErrorHint message={error} />
      </ChartCard>
    );
  }


  if (!rows || !rows.length) {
    return (
      <ChartCard title="Membership changes" subtitle="Members added / removed in range" span={6}>
        <EmptyHint>No membership changes recorded.</EmptyHint>
      </ChartCard>
    );
  }
  const tone = (op) =>
    op === "RemoveOrganizationUser" ? "danger" :
      op === "AddTeam" ? "success" :
        "info";
  return (
    <ChartCard
      title="Membership changes"
      subtitle={`${rows.length} change${rows.length === 1 ? "" : "s"} in this team's roster`}
      span={6}
    >
      <div className="ysd-table__scroll" style={{ maxHeight: 320 }}>
        <table className="ysd-table" aria-label="Membership changes">
          <thead>
            <tr>
              <th>When</th>
              <th>Op</th>
              <th>Target</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={i}>
                <td><span className="ysd-mono">{relativeTime(c.at)}</span></td>
                <td><Badge tone={tone(c.op)} dot>{c.op}</Badge></td>
                <td>{c.target_email}</td>
                <td><span className="ysd-mono">{c.actor}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

// ── Subject snapshot stats (embedded in identity header) + activity strip ──
// We split what was a 9-card grid into two leaner surfaces:
//   1. snapshot fields (storage / files / teams / roles) live INSIDE the
//      identity header — they describe who the subject is, not what they did
//   2. range-aware fields (downloaded / uploaded / events / countries /
//      touched) sit in a thin "this range" strip below the header — quick
//      glance at activity before any panel renders

const subjectStats = (subjectView) => {
  const bundle = subjectView.bundle || {};
  const events = bundle.events || [];
  const files = bundle.filesTouched || [];
  const bytesIn = files.reduce((a, r) => a + (r.bytes_in || 0), 0);
  const bytesOut = files.reduce((a, r) => a + (r.bytes_out || 0), 0);
  const distinctCountries = new Set(events.map((e) => e.country).filter(Boolean)).size;

  if (subjectView.kind === "user") {
    const u = subjectView.raw;
    return {
      storage: u.is_actual && !u.storage_mb ? null : humanBytes((u.storage_mb || 0) * 1024 * 1024),
      files: u.is_actual && !u.files ? null : (u.files || 0).toLocaleString(),
      teams: (u.teams || []).length,
      roles: u.role ? 1 : 0,
      downloaded: bytesOut ? humanBytes(bytesOut) : "—",
      uploaded: bytesIn ? humanBytes(bytesIn) : "—",
      events: events.length,
      countries: distinctCountries,
      touched: files.length,
    };
  }
  const t = subjectView.raw;
  return {
    storage: "—",
    files: "—",
    teams: 1,
    roles: (t.roles || []).length,
    downloaded: bytesOut ? humanBytes(bytesOut) : "—",
    uploaded: bytesIn ? humanBytes(bytesIn) : "—",
    events: events.length,
    countries: distinctCountries,
    touched: files.length,
  };
};

// Snapshot stats line embedded inside the identity header — describes the
// subject (storage allocated, files owned, teams, roles). One row of inline
// numbers under the role/location line.
const SubjectSnapshotLine = ({ subjectView }) => {
  const s = useMemo(() => subjectStats(subjectView), [subjectView]);
  const items = subjectView.kind === "user"
    ? [
      s.storage !== null ? { value: s.storage, label: "stored" } : null,
      s.files !== null ? { value: s.files, label: "files" } : null,
      { value: s.teams, label: s.teams === 1 ? "team" : "teams" },
      { value: s.roles, label: s.roles === 1 ? "role" : "roles" },
    ].filter(Boolean)
    : [
      { value: subjectView.raw?.member_count ?? subjectView.raw?.members?.length ?? 0, label: "members" },
      { value: s.roles, label: s.roles === 1 ? "role" : "roles" },
    ];
  return (
    <div className="users-page__head-stats" aria-label="Subject snapshot">
      {items.map((it, i) => (
        <React.Fragment key={it.label}>
          {i > 0 && <span className="users-page__head-stats-sep">·</span>}
          <span className="users-page__head-stats-item">
            <strong>{it.value}</strong> {it.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

// Compact horizontal strip of range-aware activity stats. Sits just below the
// identity header. Replaces what was previously a noisy KPI card grid.
const SubjectActivityStrip = ({ subjectView, range, dataAnchor }) => {
  const [sdkStats, setSdkStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const subjectKind = subjectView?.kind;
  const subjectKey = subjectView?.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkStats(null);
      return;
    }
    let active = true;
    setLoading(true);
    const win = rangeWindow(range, dataAnchor);
    UserOverview(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const item = items[0];
        if (!item) {
          setSdkStats(null);
          return;
        }
        setSdkStats({
          downloaded: item.downloadedBytes ? humanBytes(Number(item.downloadedBytes)) : "—",
          uploaded: item.uploadedBytes ? humanBytes(Number(item.uploadedBytes)) : "—",
          events: Number(item.eventsCount ?? 0),
          countries: Number(item.locations ?? 0),
          touched: Number(item.filesTouched ?? 0),
        });
      })
      .catch(() => {
        if (active) setSdkStats(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleStats = useMemo(() => subjectStats(subjectView), [subjectView]);
  const s = (subjectKind === "user" && sdkStats) ? sdkStats : bundleStats;

  const items = [
    { glyph: "↓", label: "downloaded", value: s.downloaded },
    { glyph: "↑", label: "uploaded", value: s.uploaded },
    { glyph: "•", label: "events", value: s.events },
    { glyph: "•", label: "countries", value: s.countries },
    { glyph: "•", label: "files touched", value: s.touched },
  ];

  if (loading) {
    return (
      <div className="users-activity-strip" role="status" aria-label="Activity in selected range">
        <span className="users-activity-strip__title">THIS RANGE</span>
        <span className="users-activity-strip__item">Loading stats...</span>
      </div>
    );
  }

  return (
    <div className="users-activity-strip" role="status" aria-label="Activity in selected range">
      <span className="users-activity-strip__title">THIS RANGE</span>
      {items.map((it) => (
        <span key={it.label} className="users-activity-strip__item">
          <span className="users-activity-strip__glyph">{it.glyph}</span>
          <strong>{it.value}</strong>
          <small>{it.label}</small>
        </span>
      ))}
    </div>
  );
};

// ── Subject storage breakdown ───────────────────────────────────────────────
// Treemap mirroring Overview's Storage distribution (SummaryDashboard's
// storageOption) but scoped to the selected subject's owned files. Toggles
// between "Size" (bytes) and "Count" (file count). Source:
//   - user subject: FILES.filter(f => f.owner === user.email)
//   - team subject: union across team members' owned files
//
// We don't pull from bundle.filesTouched here because that's range-aware
// activity, not the static "what they own" snapshot the analyst expects from
// a storage-distribution view.
const SubjectStorageBreakdownCard = ({ subjectView, palette, allUsers, range, dataAnchor }) => {
  const [metric, setMetric] = useState("size"); // "size" | "count"
  const [sdkBreakdown, setSdkBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkBreakdown(null);
      return;
    }
    let active = true;
    setLoading(true);
    const win = rangeWindow(range, dataAnchor);
    UserStorageBreakdown(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((item) => ({
          name: item.type || "other",
          size: Number(item.totBytes ?? item.tot_bytes ?? 0),
          count: Number(item.count ?? 0),
        }));
        setSdkBreakdown(mapped);
      })
      .catch(() => {
        if (active) setSdkBreakdown(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);

  const bundleOwnedFiles = useMemo(() => {
    if (subjectView.kind === "user") {
      return FILES.filter((f) => f.owner === subjectView.key);
    }
    const memberEmails = new Set(subjectView.raw?.members || []);
    if (!memberEmails.size) return [];
    return FILES.filter((f) => memberEmails.has(f.owner));
  }, [subjectView]);

  const bundleBreakdown = useMemo(() => {
    const acc = {};
    bundleOwnedFiles.forEach((f) => {
      const t = f.file_type || "other";
      if (!acc[t]) acc[t] = { name: t, size: 0, count: 0 };
      acc[t].size += (f.size || 0);
      acc[t].count += 1;
    });
    return Object.values(acc);
  }, [bundleOwnedFiles]);

  const breakdown = (subjectKind === "user" && sdkBreakdown) ? sdkBreakdown : bundleBreakdown;
  const totalSize = breakdown.reduce((a, b) => a + b.size, 0);
  const totalCount = breakdown.reduce((a, b) => a + b.count, 0);

  const right = (
    <div className="ysd-toggle">
      <button className={metric === "size" ? "active" : ""} onClick={() => setMetric("size")}>Size</button>
      <button className={metric === "count" ? "active" : ""} onClick={() => setMetric("count")}>Count</button>
    </div>
  );

  if (loading) {
    return (
      <ChartCard title="Storage breakdown" subtitle="Loading storage breakdown..." right={right}>
        <Loader message="Loading storage data…" height="320px" />
      </ChartCard>
    );
  }

  if (!breakdown.length) {
    return (
      <ChartCard
        title="Storage breakdown"
        subtitle={subjectView.kind === "team" ? "Files owned by team members, by file type" : "Files this user owns, by file type"}
        right={right}
      >
        <EmptyHint>No owned files for this subject in the catalog.</EmptyHint>
      </ChartCard>
    );
  }

  // Match Overview's storageOption exactly so the two dashboards feel like
  // the same chart pattern at different scopes.
  const measureUnit = metric === "size" ? "" : "files";
  const data = breakdown.map((b) => ({
    name: b.name,
    value: metric === "size" ? b.size : b.count,
  }));

  const option = {
    backgroundColor: "transparent",
    color: TABLEAU_10,
    tooltip: {
      backgroundColor: palette.tooltipBg,
      borderColor: palette.tooltipBorder,
      textStyle: { color: palette.text },
      formatter: (p) => {
        const display = metric === "size" ? humanBytes(p.value) : `${p.value.toLocaleString()} ${measureUnit}`;
        return `${p.marker} <b>${p.name}</b><br/>${display}`;
      },
    },
    series: [{
      type: "treemap",
      roam: false,
      nodeClick: false,
      breadcrumb: { show: false },
      data,
      label: {
        position: "insideTopLeft",
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        formatter: (p) => {
          const display = metric === "size" ? humanBytes(p.value) : `${p.value.toLocaleString()} ${measureUnit}`;
          return `${p.name}\n${display}`;
        },
      },
      upperLabel: { show: false },
      levels: [{ itemStyle: { borderColor: palette.cardBg, borderWidth: 2, gapWidth: 2 } }],
    }],
  };

  const subtitle = `${totalCount} file${totalCount === 1 ? "" : "s"} · ${humanBytes(totalSize)} · ${breakdown.length} type${breakdown.length === 1 ? "" : "s"}`;

  return (
    <ChartCard
      title="Storage breakdown"
      subtitle={subtitle}
      right={right}
    >
      <EChart option={option} height={320} />
    </ChartCard>
  );
};

// ── Storage timeline ────────────────────────────────────────────────────────
// Cumulative bytes-in (write volume) over the active range, layered with a
// daily upload-bytes bar. Mirrors how analysts read storage growth: the line
// shows the rising baseline; the bars show which day spiked it.
const StorageTimelineCard = ({ subjectView, palette, range, dataAnchor }) => {
  const [sdkSeries, setSdkSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subjectKind = subjectView.kind;
  const subjectKey = subjectView.key;

  useEffect(() => {
    if (subjectKind !== "user") {
      setSdkSeries(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const win = rangeWindow(range, dataAnchor);
    UserWriteActivity(subjectKey, win.start, win.end)
      .then((items) => {
        if (!active) return;
        const mapped = (items || []).map((item) => ({
          date: item.date || "Unknown",
          totBytes: Number(item.totBytes ?? item.tot_bytes ?? 0),
        }));

        if (!mapped.length) {
          setSdkSeries(null);
          return;
        }

        // Ensure sorted by date
        mapped.sort((a, b) => a.date.localeCompare(b.date));

        const days = [];
        const daily = [];
        const cumulative = [];
        let running = 0;

        mapped.forEach((m) => {
          days.push(m.date);
          daily.push(m.totBytes);
          running += m.totBytes;
          cumulative.push(running);
        });

        setSdkSeries({ days, daily, cumulative, total: running });
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load storage activity");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [subjectKind, subjectKey, range, dataAnchor]);


  const bundleSeries = useMemo(() => {
    // Aggregate FinalizeWrite + StartWrite event sizes per UTC day.
    const events = subjectView.bundle.events || [];
    const dayOf = (ts) => new Date(ts).toISOString().slice(0, 10);
    const buckets = new Map();
    events.forEach((e) => {
      const sz = e.subject?.finalizeWrite?.size || e.subject?.startWrite?.size || 0;
      if (!sz) return;
      const k = dayOf(e.timestamp);
      buckets.set(k, (buckets.get(k) || 0) + sz);
    });
    if (!buckets.size) return null;
    const days = [...buckets.keys()].sort();
    let running = 0;
    const cumulative = days.map((d) => { running += buckets.get(d) || 0; return running; });
    const daily = days.map((d) => buckets.get(d) || 0);
    return { days, daily, cumulative, total: running };
  }, [subjectView.bundle.events]);

  const series = (subjectKind === "user" && sdkSeries) ? sdkSeries : bundleSeries;

  const subtitle = subjectView.kind === "user"
    ? `Snapshot ${humanBytes((subjectView.raw.storage_mb || 0) * 1024 * 1024)} total · upload activity in this range below`
    : "Aggregate write activity across team members in this range";

  if (!series) {
    return (
      <ChartCard title="Storage usage" subtitle={subtitle} span={6}>
        <EmptyHint>No write activity in the selected range.</EmptyHint>
      </ChartCard>
    );
  }

  const option = {
    backgroundColor: "transparent",
    grid: { top: 30, right: 50, bottom: 36, left: 56 },
    tooltip: {
      trigger: "axis",
      backgroundColor: palette.tooltipBg,
      borderColor: palette.tooltipBorder,
      textStyle: { color: palette.text },
      formatter: (params) => {
        const date = params[0]?.axisValue;
        const lines = params.map((p) => `${p.marker} ${p.seriesName}: <b>${humanBytes(p.value)}</b>`);
        return `${date}<br/>${lines.join("<br/>")}`;
      },
    },
    legend: { top: 0, textStyle: { color: palette.textSoft, fontSize: 11 }, icon: "roundRect" },
    xAxis: {
      type: "category",
      data: series.days,
      axisLine: { lineStyle: { color: palette.grid } },
      axisLabel: { color: palette.textSoft, fontSize: 10 },
    },
    yAxis: [
      {
        type: "value",
        name: "daily",
        position: "left",
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 10, formatter: (v) => humanBytes(v) },
      },
      {
        type: "value",
        name: "cumulative",
        position: "right",
        splitLine: { show: false },
        axisLabel: { color: palette.textSoft, fontSize: 10, formatter: (v) => humanBytes(v) },
      },
    ],
    series: [
      {
        name: "Daily writes",
        type: "bar",
        yAxisIndex: 0,
        data: series.daily,
        itemStyle: { color: palette.brandSoft, borderRadius: [4, 4, 0, 0] },
      },
      {
        name: "Cumulative",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "none",
        data: series.cumulative,
        lineStyle: { width: 2.5, color: palette.brand },
        areaStyle: { color: "rgba(130, 55, 187, 0.10)" },
      },
    ],
  };

  return (
    <ChartCard
      title="Storage usage"
      subtitle={`${humanBytes(series?.total || 0)} written in window · ${subtitle}`}
      span={6}
    >
      {loading ? <Loader message="Loading storage activity…" minHeight={280} /> :
        error ? <ErrorHint message={error} /> :
          series ? <EChart option={option} height={280} /> : <EmptyHint>No write activity recorded.</EmptyHint>}
    </ChartCard>

  );
};

// ── Export PDF ──────────────────────────────────────────────────────────────
// Data-driven PDF report — mirrors FilesDashboard's exportPdf shape. jsPDF +
// jspdf-autotable, lazy-loaded so the import doesn't bloat the bundle.
//
// Sections (in order): cover / metadata / summary / risk / anomalies /
// timeline / where / when / op mix / files-touched / shares / permissions /
// denied / footer. Selectable text in the result.
const ExportPdfButton = ({ subjectView, anomalyView, range, dataAnchor }) => {
  const [exporting, setExporting] = useState(false);

  const onClick = async () => {
    setExporting(true);
    try {
      // The dashboard cards each fetch their own SDK data; the export only
      // sees `subjectView.bundle` and `anomalyView` which come from
      // never-populated mocks / local engines run on those mocks. Pull the SDK
      // data inline so the PDF mirrors what the analyst sees.
      let enrichedSubject = subjectView;
      let enrichedAnomaly = anomalyView;
      if (subjectView.kind === "user") {
        const [bundle, sdkAnomaly] = await Promise.all([
          fetchLiveBundle(subjectView, range, dataAnchor),
          fetchLiveAnomalyView(subjectView, range, dataAnchor),
        ]);
        enrichedSubject = { ...subjectView, bundle };
        enrichedAnomaly = sdkAnomaly;
      }
      await exportSubjectPdf(enrichedSubject, enrichedAnomaly);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Users PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      className="users-page__action-btn users-page__action-btn--primary"
      onClick={onClick}
      disabled={exporting}
    >
      {exporting ? "Exporting…" : "Export PDF"}
    </button>
  );
};

// Risk score + anomaly list — mirrors RiskScoreCard / AnomaliesCard mappings.
// Returns the same `{ score, anomalies }` shape `exportSubjectPdf` already
// reads off `anomalyView`, so the export tables/sections render unchanged.
const RISK_SIGNAL_META = {
  total_events: { weight: 0, label: "High event volume" },
  failed_operations: { weight: 20, label: "Failed operations spike" },
  off_hours_access: { weight: 15, label: "Off-hours access" },
  mass_download: { weight: 30, label: "Mass download" },
  distinct_locations: { weight: 10, label: "Multiple login locations" },
};

const ANOMALY_META = {
  bulk_download: { title: "Bulk download", severity: "high", weight: 35 },
  bulk_delete: { title: "Bulk delete", severity: "high", weight: 40 },
  external_share_spike: { title: "External share spike", severity: "medium", weight: 25 },
};

async function fetchLiveAnomalyView(subjectView, range, dataAnchor) {
  const win = rangeWindow(range, dataAnchor);
  const subjectKey = subjectView.key;
  const safe = (p) => p.catch(() => null);

  const [scoreItems, anomalyItems] = await Promise.all([
    safe(UserRiskScore(subjectKey, win.start, win.end)),
    safe(UserAnomalies(subjectKey, win.start, win.end)),
  ]);

  let total = 0;
  const signals = [];
  (scoreItems || []).forEach((item) => {
    const kind = item.signal || "unknown";
    const meta = RISK_SIGNAL_META[kind] || { weight: 0, label: kind };
    if (meta.weight > 0) {
      total += meta.weight;
      signals.push({ kind, label: meta.label, weight: meta.weight });
    }
  });
  const score = {
    total: Math.min(100, total),
    band: total > 60 ? "high" : total > 30 ? "medium" : "low",
    weekOverWeek: { deltaPct: 0, previous: 0 },
    signals: signals.sort((a, b) => b.weight - a.weight),
  };

  const fmtMs = (ms) => (ms ? new Date(ms).toLocaleDateString() : "?");
  const anomalies = (anomalyItems || []).map((item, idx) => {
    const type = item.anomalyType || item.anomaly_type || "unknown";
    const startMs = Number(item.startTime ?? item.start_time ?? 0);
    const endMs = Number(item.endTime ?? item.end_time ?? 0);
    const count = Number(item.count ?? 0);
    const meta = ANOMALY_META[type] || { title: type, severity: "medium", weight: 15 };
    return {
      id: `anom_${type}_${idx}`,
      severity: meta.severity,
      title: meta.title,
      description: `${count.toLocaleString()} events · ${fmtMs(startMs)} → ${fmtMs(endMs)}`,
      weight: meta.weight,
      first_seen: startMs,
      last_seen: endMs,
      evidence: { panel: type === "bulk_delete" ? "files" : type === "external_share_spike" ? "shares" : "files", event_ids: [] },
    };
  });

  return { score, anomalies };
}

// Mirrors the per-card SDK fetches (events, countries, op-mix, heatmap, files
// touched, sharing actions, denials, permissions). Field-mapping kept in
// lockstep with the cards so the PDF matches what the analyst sees on screen.
async function fetchLiveBundle(subjectView, range, dataAnchor) {
  const win = rangeWindow(range, dataAnchor);
  const subjectKey = subjectView.key;
  const safe = (p) => p.catch(() => null);

  const [
    timeline, countries, opMix, heatmap, filesTouched,
    sharing, denied, permissions, writeActivity, storage,
  ] = await Promise.all([
    safe(UserActivityTimeline(subjectKey, win.start, win.end)),
    safe(UserCountriesAccessedFrom(subjectKey, win.start, win.end)),
    safe(UserOpCount(subjectKey, win.start, win.end)),
    safe(UserTimeHeatmap(subjectKey, win.start, win.end)),
    safe(UserFilesTouched(subjectKey, win.start, win.end)),
    safe(UserSharingActions(subjectKey, win.start, win.end)),
    safe(UserDeniedAttempts(subjectKey, win.start, win.end)),
    safe(UserPermissionRecieved(subjectKey, win.start, win.end)),
    safe(UserWriteActivity(subjectKey, win.start, win.end)),
    safe(UserStorageBreakdown(subjectKey, win.start, win.end)),
  ]);

  const events = (timeline || []).map((row) => {
    let countryStr = row.location || "Unknown";
    if (countryStr && /^\d+$/.test(String(countryStr))) {
      const n = parseInt(countryStr, 10);
      countryStr = `${(n >>> 24) & 255}.${(n >>> 16) & 255}.${(n >>> 8) & 255}.${n & 255}`;
    }
    return {
      timestamp: Number(row.timestamp) || 0,
      operation: row.operation || "Unknown",
      user_email: row.operationDoneBy || row.userEmail || subjectKey,
      actor: row.operationDoneBy || row.userEmail || subjectKey,
      country: countryStr,
      detail: row.impactedUser ? `Impacted: ${row.impactedUser}` : (row.message || ""),
      success: row.success !== undefined ? row.success : true,
    };
  });

  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  (heatmap || []).forEach(({ dow, hour, count }) => {
    if (dow >= 0 && dow <= 6 && hour >= 0 && hour <= 23) grid[dow][hour] += count;
  });

  return {
    events,
    daily: (writeActivity || []).map((d) => ({
      date: d.date || (d.timestamp ? new Date(Number(d.timestamp)).toISOString().slice(0, 10) : ""),
      reads: Number(d.reads || 0),
      writes: Number(d.writes || 0),
      shares: Number(d.shares || 0),
      deletes: Number(d.deletes || 0),
    })),
    hourHeatmap: grid,
    opMix: (opMix || []).map((item) => ({
      operation: item.operation || item.op || "Unknown",
      count: Number(item.count || 0),
    })),
    countries: (countries || []).map((c) => ({
      country: c.location || "Unknown",
      count: Number(c.count || 0),
      firstSeen: win.start,
      lastSeen: win.end,
      trustTier: "allowed",
    })),
    sessions: [],
    filesTouched: (filesTouched || []).map((r) => ({
      path: r.objectPath || r.object_path || r.path || "Unknown",
      file_type: r.fileType || r.file_type || "other",
      reads: Number(r.reads || 0),
      writes: Number(r.writes || 0),
      shares: Number(r.shares || 0),
      deletes: Number(r.deletes || 0),
      bytes_in: Number(r.bytesIn ?? r.bytes_in ?? 0),
      bytes_out: Number(r.bytesOut ?? r.bytes_out ?? 0),
      last_touched: Number(r.lastTouched ?? r.last_touched ?? 0),
      tags: r.tags || [],
    })),
    sharesGranted: (sharing || []).map((s) => ({
      at: Number(s.timestamp || 0),
      object_path: s.object_path || "Unknown",
      recipient: s.recipient || "Unknown",
      recipient_type: s.recipient_type || "user",
      granted_by: { is_self: s.granted_by === "self" || s.granted_by === subjectKey, teams: [], roles: [] },
      success: s.success !== false,
    })),
    deniedAttempts: (denied || []).map((e) => ({
      timestamp: Number(e.timestamp ?? 0),
      operation: e.operation || "Unknown",
      object_path: e.objectPath || "",
      country: e.ipAddress ? "IP: " + e.ipAddress : "Unknown",
      message: e.message || "Access denied",
    })),
    permissionsReceived: [
      ...((permissions?.roles || []).map((r) => ({ role: r, team: "" }))),
      ...((permissions?.teams || []).map((t) => ({ role: "", team: t }))),
    ],
    permissionsDelta: { added: [], removed: [] },
    storageBreakdown: (storage || []).map((item) => ({
      name: item.type || "other",
      size: Number(item.totBytes ?? item.tot_bytes ?? 0),
      count: Number(item.count ?? 0),
    })),
    baseline: { countriesEverSeen: [], lastActiveBeforeRange: 0 },
  };
}

async function exportSubjectPdf(subjectView, anomalyView) {
  const { jsPDF } = await import("jspdf");
  const autoTableMod = await import("jspdf-autotable");
  const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;
  const { renderChartToImage, addChartImage } = await import("./_shared/chartImage");

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 36;
  const brand = [130, 55, 187];

  // Light palette tuned for the embedded charts so the PDF stays readable
  // regardless of the analyst's current dashboard theme.
  const pdfPalette = {
    text: "#374151", textSoft: "#6b7280", grid: "#e5e7eb",
    cardBg: "#ffffff", tooltipBg: "#ffffff", tooltipBorder: "#d1d5db",
    danger: "#e15759", warning: "#f59e0b", success: "#2ca02c",
    brand: "#8237bb", brandSoft: "#b078ff",
  };

  const subjectKindLabel = subjectView.kind === "team" ? "Team" : "User";
  const safeKey = (subjectView.key || "subject").replace(/[^a-z0-9-_.]/gi, "_");
  const stamp = new Date().toISOString().slice(0, 10);

  // ── Cover ─────────────────────────────────────────────────────────────────
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(...brand);
  pdf.text(`${subjectKindLabel} Activity Report`, margin, margin + 6);
  pdf.setFontSize(13);
  pdf.setTextColor(40);
  pdf.text(subjectView.title, margin, margin + 26);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(110);
  pdf.text(
    `Generated ${new Date().toLocaleString()}${subjectView.subtitle ? ` · ${subjectView.subtitle}` : ""}`,
    margin, margin + 42,
  );

  const tableOpts = {
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: brand, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 244, 253] },
  };
  const startY = (afterPrev = 18) =>
    (pdf.lastAutoTable ? pdf.lastAutoTable.finalY : margin + 56) + afterPrev;
  const sectionHeader = (title) => {
    const y = startY(22);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(...brand);
    pdf.text(title, margin, y);
    pdf.setDrawColor(...brand);
    pdf.setLineWidth(0.6);
    pdf.line(margin, y + 3, pageW - margin, y + 3);
    return y + 10;
  };

  const bundle = subjectView.bundle;
  const events = bundle.events || [];

  // ── Subject metadata ─────────────────────────────────────────────────────
  if (subjectView.kind === "user") {
    const u = subjectView.raw;
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader("Subject details"),
      head: [["Field", "Value"]],
      body: [
        ["Name", subjectView.title],
        ["Email", u.email],
        ["Role", u.role || "—"],
        ["Teams", (u.teams || []).join(", ") || "—"],
        ["Location", u.location || "—"],
        ["Primary country", u.primary_country || "—"],
        ["Timezone", u.timezone || "—"],
        ["Status", subjectView.status || "—"],
        ["Joined", u.joined || "—"],
        ["Account age (days)", String(u.account_age_days ?? "—")],
        ["Last active", u.last_active ? `${new Date(u.last_active).toLocaleString()} (${relativeTime(u.last_active)})` : "—"],
        // conditionally filter fields that are empty for real users
        ...(u.is_actual && !u.storage_mb ? [] : [["Storage", typeof u.storage_mb === "number" ? humanBytes(u.storage_mb * 1024 * 1024) : "—"]]),
        ...(u.is_actual && !u.files ? [] : [["Files (total)", String(u.files ?? "—")]]),
      ],
      columnStyles: { 0: { cellWidth: 140, fontStyle: "bold" } },
    });
  } else {
    const t = subjectView.raw;
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader("Team details"),
      head: [["Field", "Value"]],
      body: [
        ["Name", t.name],
        ["Members", String(t.member_count ?? t.members?.length ?? 0)],
        ["Roles", (t.roles || []).join(", ") || "—"],
        ["Owner", t.owner_email || "—"],
      ],
      columnStyles: { 0: { cellWidth: 140, fontStyle: "bold" } },
    });
  }

  // ── Summary ──────────────────────────────────────────────────────────────
  const distinctActors = new Set(events.map((e) => e.user_email).filter(Boolean));
  const distinctCountries = new Set(events.map((e) => e.country).filter(Boolean));
  const denied = (bundle.deniedAttempts || []).length;
  const reads = events.filter((e) => e.operation === "GetChunk").length;
  const writes = events.filter((e) => /Write|RestoreVersion/.test(e.operation)).length;
  const sharesCount = (bundle.sharesGranted || []).length;
  const externalShares = (bundle.sharesGranted || []).filter((s) => s.recipient_type === "external").length;
  const filesTouchedCount = (bundle.filesTouched || []).length;

  autoTable(pdf, {
    ...tableOpts,
    startY: sectionHeader("Summary"),
    head: [["Metric", "Value"]],
    body: [
      ["Total events", String(events.length)],
      ["Distinct actors (team agg)", String(distinctActors.size)],
      ["Distinct countries", String(distinctCountries.size)],
      ["Reads", String(reads)],
      ["Writes", String(writes)],
      ["Shares granted", `${sharesCount} (${externalShares} external)`],
      ["Files touched", String(filesTouchedCount)],
      ["Denied attempts", String(denied)],
    ],
    columnStyles: { 0: { cellWidth: 200, fontStyle: "bold" } },
  });

  // ── Risk score + signals ─────────────────────────────────────────────────
  if (anomalyView?.score) {
    const score = anomalyView.score;
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader("Risk score"),
      head: [["Field", "Value"]],
      body: [
        ["Total", `${score.total} / 100`],
        ["Band", score.band],
        ["Previous (W/W)", String(score.weekOverWeek.previous)],
        ["Delta", `${score.weekOverWeek.deltaPct >= 0 ? "+" : ""}${score.weekOverWeek.deltaPct}%`],
        ["Signals contributing", String(score.signals.length)],
      ],
      columnStyles: { 0: { cellWidth: 200, fontStyle: "bold" } },
    });
    if (score.signals.length) {
      autoTable(pdf, {
        ...tableOpts,
        startY: startY(8),
        head: [["Signal", "Weight"]],
        body: score.signals.map((s) => [s.label, `+${s.weight}`]),
        columnStyles: { 1: { cellWidth: 80, halign: "right" } },
      });
    }
  }

  // ── Anomalies ────────────────────────────────────────────────────────────
  const anomalies = anomalyView?.anomalies || [];
  if (anomalies.length) {
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader(`Anomalies (${anomalies.length})`),
      head: [["Severity", "Title", "Description", "First seen", "Last seen", "Weight"]],
      body: anomalies.map((a) => [
        a.severity,
        a.title,
        a.description,
        a.first_seen ? new Date(a.first_seen).toLocaleString() : "—",
        a.last_seen ? new Date(a.last_seen).toLocaleString() : "—",
        `+${Math.round(a.weight)}`,
      ]),
      columnStyles: { 5: { cellWidth: 50, halign: "right" } },
    });
  }

  // ── Activity timeline (every event) ──────────────────────────────────────
  if (events.length) {
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader(`Activity timeline (${events.length})`),
      head: [["#", "When", "Op", "Country", "Object", "Result"]],
      body: events.map((e, i) => [
        String(i + 1),
        new Date(e.timestamp).toLocaleString(),
        e.operation,
        e.country || "—",
        objectPathFor(e),
        e.success === false ? "denied" : "allowed",
      ]),
      styles: { ...tableOpts.styles, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 22, halign: "right" },
        1: { cellWidth: 95 },
        4: { cellWidth: "auto" },
        5: { cellWidth: 50 },
      },
    });
  }

  // ── Where (countries): chart + table ─────────────────────────────────────
  if ((bundle.countries || []).length) {
    const ordered = [...bundle.countries].sort((a, b) => a.count - b.count);
    const tierColor = (c) =>
      c.trustTier === "blocked" ? pdfPalette.danger :
        c.trustTier === "trusted" ? pdfPalette.success : pdfPalette.warning;
    const whereOption = {
      grid: { top: 10, right: 56, bottom: 36, left: 160 },
      xAxis: { type: "value", axisLabel: { color: pdfPalette.textSoft, fontSize: 11 }, splitLine: { lineStyle: { color: pdfPalette.grid } }, axisLine: { lineStyle: { color: pdfPalette.grid } } },
      yAxis: { type: "category", data: ordered.map((r) => r.country), axisLine: { lineStyle: { color: pdfPalette.grid } }, axisTick: { show: false }, axisLabel: { color: pdfPalette.text, fontSize: 11 } },
      series: [{
        type: "bar",
        data: ordered.map((r) => ({ value: r.count, itemStyle: { color: tierColor(r), borderRadius: [0, 6, 6, 0] } })),
        label: { show: true, position: "right", color: pdfPalette.text, fontSize: 11 },
      }],
    };
    sectionHeader("Where");
    const img = await renderChartToImage(whereOption, { width: 900, height: 360 });
    addChartImage(pdf, img, { margin });
    autoTable(pdf, {
      ...tableOpts,
      startY: pdf.lastAutoTable.finalY + 10,
      head: [["Country", "Events", "Trust tier", "First seen", "Last seen"]],
      body: bundle.countries.map((c) => [
        c.country,
        String(c.count),
        c.trustTier,
        new Date(c.firstSeen).toLocaleString(),
        new Date(c.lastSeen).toLocaleString(),
      ]),
      columnStyles: { 1: { cellWidth: 60, halign: "right" } },
    });
  }

  // ── When (heatmap) ───────────────────────────────────
  const grid = bundle.hourHeatmap || [];
  const totalHm = grid.reduce((s, r) => s + r.reduce((a, n) => a + n, 0), 0);
  if (totalHm > 0) {
    const HOURS = Array.from({ length: 24 }, (_, h) => h);
    const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader("When (UTC hour × day-of-week)"),
      head: [["Day", ...HOURS.map((h) => String(h))]],
      body: grid.map((row, dow) => [DAYS[dow], ...row.map((n) => (n ? String(n) : ""))]),
      styles: { ...tableOpts.styles, fontSize: 7, cellPadding: 2, halign: "center" },
      columnStyles: { 0: { cellWidth: 28, fontStyle: "bold", halign: "left" } },
    });
  }

  // ── Op mix: donut + table ───────────────────────────────────────────────
  if ((bundle.opMix || []).length) {
    // Group by class (mirroring the dashboard's OpMixCard) so the donut tells
    // a useful story instead of being one slice per individual op.
    const byClass = {};
    bundle.opMix.forEach(({ operation, count }) => {
      const cls = OP_TO_CLASS[operation] || "other";
      byClass[cls] = (byClass[cls] || 0) + count;
    });
    const opMixOption = {
      tooltip: {},
      legend: { type: "scroll", orient: "horizontal", bottom: 0, left: "center", textStyle: { color: pdfPalette.text, fontSize: 11 }, itemWidth: 10, itemHeight: 10, itemGap: 14 },
      series: [{
        type: "pie",
        radius: ["48%", "70%"],
        center: ["50%", "44%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: pdfPalette.cardBg, borderWidth: 2, borderRadius: 4 },
        label: { show: false },
        labelLine: { show: false },
        data: Object.entries(byClass).map(([name, value]) => ({
          name, value,
          itemStyle: { color: OP_CLASS_COLORS[name] || "#cbd5e1" },
        })),
      }],
    };
    sectionHeader("Operation mix");
    const img = await renderChartToImage(opMixOption, { width: 720, height: 360 });
    addChartImage(pdf, img, { margin });
    autoTable(pdf, {
      ...tableOpts,
      startY: pdf.lastAutoTable.finalY + 10,
      head: [["Operation", "Count"]],
      body: bundle.opMix.map((o) => [o.operation, String(o.count)]),
      columnStyles: { 1: { cellWidth: 70, halign: "right" } },
    });
  }

  // ── Activity over time (NEW chart): daily reads/writes/shares/deletes ───
  if ((bundle.daily || []).length > 1) {
    const days = bundle.daily.map((d) => d.date);
    const reads = bundle.daily.map((d) => d.reads || 0);
    const writes = bundle.daily.map((d) => d.writes || 0);
    const shares = bundle.daily.map((d) => d.shares || 0);
    const deletes = bundle.daily.map((d) => d.deletes || 0);
    const activityOption = {
      tooltip: { trigger: "axis" },
      legend: { top: 0, textStyle: { color: pdfPalette.textSoft, fontSize: 11 }, icon: "roundRect" },
      grid: { top: 36, right: 16, bottom: 28, left: 40 },
      xAxis: { type: "category", data: days, axisLine: { lineStyle: { color: pdfPalette.grid } }, axisLabel: { color: pdfPalette.textSoft, fontSize: 10 } },
      yAxis: { type: "value", splitLine: { lineStyle: { color: pdfPalette.grid } }, axisLabel: { color: pdfPalette.textSoft, fontSize: 10 } },
      series: [
        { name: "Reads", type: "line", smooth: true, symbol: "none", data: reads, lineStyle: { width: 2, color: OP_CLASS_COLORS.read } },
        { name: "Writes", type: "line", smooth: true, symbol: "none", data: writes, lineStyle: { width: 2, color: OP_CLASS_COLORS.write } },
        { name: "Shares", type: "line", smooth: true, symbol: "none", data: shares, lineStyle: { width: 2, color: OP_CLASS_COLORS.share } },
        { name: "Deletes", type: "line", smooth: true, symbol: "none", data: deletes, lineStyle: { width: 2, color: OP_CLASS_COLORS.destructive } },
      ],
    };
    sectionHeader("Activity over time");
    const img = await renderChartToImage(activityOption, { width: 900, height: 320 });
    addChartImage(pdf, img, { margin });
  }

  // ── Storage breakdown by file type (NEW chart): treemap ─────────────────
  // Prefer SDK-driven breakdown carried on the bundle; fall back to FILES (mock).
  let breakdown = bundle.storageBreakdown || [];
  if (!breakdown.length) {
    const ownedFiles = subjectView.kind === "user"
      ? FILES.filter((f) => f.owner === subjectView.key)
      : FILES.filter((f) => new Set(subjectView.raw?.members || []).has(f.owner));
    const buckets = {};
    ownedFiles.forEach((f) => {
      const t = f.file_type || "other";
      if (!buckets[t]) buckets[t] = { name: t, size: 0, count: 0 };
      buckets[t].size += (f.size || 0);
      buckets[t].count += 1;
    });
    breakdown = Object.values(buckets);
  }
  if (breakdown.length) {
    const treemapOption = {
      tooltip: {},
      color: TABLEAU_10,
      series: [{
        type: "treemap",
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        data: breakdown.map((b) => ({ name: b.name, value: b.size })),
        label: {
          position: "insideTopLeft",
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          formatter: (p) => `${p.name}\n${humanBytes(p.value)}`,
        },
        upperLabel: { show: false },
        levels: [{ itemStyle: { borderColor: "#ffffff", borderWidth: 2, gapWidth: 2 } }],
      }],
    };
    sectionHeader("Storage breakdown");
    const img = await renderChartToImage(treemapOption, { width: 900, height: 360 });
    addChartImage(pdf, img, { margin });
    autoTable(pdf, {
      ...tableOpts,
      startY: pdf.lastAutoTable.finalY + 10,
      head: [["File type", "Files", "Total size"]],
      body: breakdown
        .sort((a, b) => b.size - a.size)
        .map((b) => [b.name, String(b.count), humanBytes(b.size)]),
      columnStyles: { 1: { cellWidth: 70, halign: "right" }, 2: { cellWidth: 100, halign: "right" } },
    });
  }

  // ── Files touched (top 50 by bytes-out) ──────────────────────────────────
  if ((bundle.filesTouched || []).length) {
    const top = [...bundle.filesTouched].sort((a, b) => b.bytes_out - a.bytes_out).slice(0, 50);
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader(`Files touched (top ${top.length})`),
      head: [["Path", "R", "W", "S", "D", "Bytes out", "Bytes in", "Last touched", "Tags"]],
      body: top.map((r) => [
        r.path,
        String(r.reads), String(r.writes), String(r.shares), String(r.deletes),
        r.bytes_out ? humanBytes(r.bytes_out) : "—",
        r.bytes_in ? humanBytes(r.bytes_in) : "—",
        new Date(r.last_touched).toLocaleString(),
        (r.tags || []).join(", ") || "—",
      ]),
      styles: { ...tableOpts.styles, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 22, halign: "right" },
        2: { cellWidth: 22, halign: "right" },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: 22, halign: "right" },
        5: { cellWidth: 60, halign: "right" },
        6: { cellWidth: 60, halign: "right" },
      },
    });
  }

  // ── Sharing actions granted ──────────────────────────────────────────────
  if ((bundle.sharesGranted || []).length) {
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader(`Sharing actions (${bundle.sharesGranted.length})`),
      head: [["When", "Object", "Recipient", "Type", "Granted by"]],
      body: bundle.sharesGranted.map((s) => [
        new Date(s.at).toLocaleString(),
        s.object_path,
        s.recipient || "—",
        s.recipient_type,
        grantedByText(s.granted_by),
      ]),
    });
  }

  // ── Permissions received ─────────────────────────────────────────────────
  if (subjectView.kind === "user") {
    const edges = bundle.permissionsReceived || [];
    const delta = bundle.permissionsDelta || { added: [], removed: [] };
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader("Permissions received"),
      head: [["Source", "Name"]],
      body: edges.map((e) => [e.role ? "role" : "team", e.role || e.team]),
      columnStyles: { 0: { cellWidth: 60 } },
    });
    if (delta.added.length || delta.removed.length) {
      autoTable(pdf, {
        ...tableOpts,
        startY: startY(8),
        head: [["Change", "Source", "Name"]],
        body: [
          ...delta.added.map((e) => ["added", e.role ? "role" : "team", e.role || e.team]),
          ...delta.removed.map((e) => ["removed", e.role ? "role" : "team", e.role || e.team]),
        ],
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 60 } },
      });
    }
  }

  // ── Denied attempts ──────────────────────────────────────────────────────
  if ((bundle.deniedAttempts || []).length) {
    autoTable(pdf, {
      ...tableOpts,
      startY: sectionHeader(`Denied attempts (${bundle.deniedAttempts.length})`),
      head: [["When", "Op", "Object", "Country", "Reason"]],
      body: bundle.deniedAttempts.map((e) => [
        new Date(e.timestamp).toLocaleString(),
        e.operation,
        objectPathFor(e),
        e.country || "—",
        e.message || "—",
      ]),
    });
  }

  // ── Footer pagination ────────────────────────────────────────────────────
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(140);
    pdf.text(
      `${subjectView.title} · Page ${i} of ${total}`,
      pageW - margin,
      pdf.internal.pageSize.getHeight() - 14,
      { align: "right" },
    );
  }

  pdf.save(`${safeKey}-activity-${stamp}.pdf`);
}

// Resolve a path for an event row. SDK-shaped rows expose `object_path` /
// `detail` directly; legacy mock rows nest a JSONB `subject` blob.
const objectPathFor = (e) => {
  if (e.object_path) return e.object_path;
  const s = e.subject || {};
  const inner = Object.values(s)[0];
  if (inner && typeof inner === "object") {
    const fromSubject =
      inner.path_of_object ||
      inner.file_path_in_owner ||
      inner.file_path ||
      inner.acl?.object_path ||
      inner.dest_path_in_subject;
    if (fromSubject) return fromSubject;
  }
  return e.detail || "—";
};

const grantedByText = (g) => {
  if (!g) return "—";
  if (g.is_self) return "self";
  if (Array.isArray(g.teams) && g.teams.length) return `team: ${g.teams.join(", ")}`;
  if (Array.isArray(g.roles) && g.roles.length) return `role: ${g.roles.join(", ")}`;
  return "—";
};

export default UsersDashboard;
